import { useEffect, useLayoutEffect, useState, Suspense, type ReactNode } from 'react'
import { Routes, Route, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import type { Session } from '@supabase/supabase-js'
import { BIN_PAGE_MODULES, hasAnyModule, hasModule, MODULES } from '@/lib/modules'
import { useMoneyLock } from '@/lib/moneyLock'
import { MoneyLockedPanel, MoneyOnly } from '@/components/MoneyLock'
import { BrandLoader, LoadingWords } from '@/components/BrandLoader'
import { trackRouteChange } from '@/lib/uiTelemetry'
import { useCurrentUser } from '@/lib/useCurrentUser'
import { usePermissions } from '@/lib/usePermissions'
import { useActiveTenant, type TenantKind } from '@/lib/useActiveTenant'
import { hasStoredSession, signOutEverywhere } from '@/lib/offlineSession'
import { useBrandChrome } from '@/lib/useTenantBrand'
import { applyAppearance } from '@/lib/appearance'
import { lazyWithReload } from '@/lib/lazyWithReload'
import { MOVED_TO_ADMIN_APP } from '@/lib/appUrls'
import TenantLockout from '@/components/TenantLockout'
import { Analytics } from '@vercel/analytics/react'
import { SpeedInsights } from '@vercel/speed-insights/react'

// Eager — KF49 packhouse operator hot path. Only the screens the floor
// tablets land on every shift stay in the initial bundle so the daily
// flow has zero chunk-fetch delay. Detail views (opened from list rows)
// + admin pages + secondary workflows lazy-load below; the chunk fetch
// happens during the in-list click which masks the latency. Login +
// AcceptInvite must stay eager so pre-auth routing has no Suspense
// boundary. Pages used by FARM / AGENT tenants lazy-load below.
import LoginPage        from '@/modules/shared/Login'
import AcceptInvitePage from '@/modules/shared/AcceptInvite'
import AuthCallbackPage from '@/modules/shared/AuthCallback'
import SignupTenantPage from '@/modules/shared/SignupTenant'
import { PUBLIC_SIGNUP_ENABLED } from '@/lib/signupFlag'
import DashboardPage    from '@/modules/shared/Dashboard'
import DeliveriesStage  from '@/modules/packhouse/stages/DeliveriesStage'
import TippingPage      from '@/modules/packhouse/Tipping'
import RunsPage         from '@/modules/packhouse/Runs'
import InspectionPage   from '@/modules/packhouse/Inspection'
import RejectsPage      from '@/modules/packhouse/Rejects'
import ColdroomPage     from '@/modules/packhouse/Coldroom'
import { PalletSearchRoot } from '@/components/GlobalPalletSearch'
import { HelpBarRoot } from '@/components/HelpBar'
import { SupportCallRoot } from '@/components/SupportCall'
import { BugReportRoot } from '@/components/BugReportForm'
import UpdateBanner from '@/components/UpdateBanner'
import InstallPrompt from '@/components/InstallPrompt'
import SupportViewBanner from '@/components/SupportViewBanner'
import QueriesPausedBanner from '@/components/QueriesPausedBanner'
import PlatformModeBanner from '@/components/PlatformModeBanner'
import TopBanners from '@/components/TopBanners'
import { ConsentGate } from '@/components/ConsentGate'
import OfflineQueueBadge from '@/components/OfflineQueueBadge'
import UndoToast from '@/components/UndoToast'
import BillingBanner from '@/components/BillingBanner'
import PullToRefresh from '@/components/PullToRefresh'
import BottomNav from '@/components/BottomNav'
import NavDebugChip from '@/components/NavDebugChip'
import TenantSwitchOverlay from '@/components/TenantSwitchOverlay'
import { SharedDeviceSignOuts } from '@/components/SharedDeviceSignOuts'

// lazyWithReload (stale-chunk auto-reload wrapper) + the global
// vite:preloadError net now live in src/lib/lazyWithReload.ts.

// Lazy — admin / reporting / rarely-opened pages. Each ships as its own
// chunk and is only fetched on first navigation to the route. Cuts the
// initial bundle by roughly 40-50%; service worker caches the chunks
// after the first fetch so subsequent visits are instant.
//
// Detail pages (DeliveryDetail / RunDetail / MissionDetail / PalletDetail)
// + secondary workflow pages (LoadingBay / Receipts / Consignments /
// Rebin / Yard / BinInventory / RejectBins) are lazy too — they're
// reached through a list click, so the chunk fetch overlaps with the
// list→detail transition and feels instant on the floor tablets.
// The 3 giant packhouse pages (Pallets ~4.5k / QC ~4k / Dispatch ~3.9k lines,
// scale audit F6). Lazy-split out of the main bundle so a deploy that doesn't
// touch them lets the SW reuse the cached chunk instead of re-downloading them
// in the shell — the "multi-second install per deploy on low-end tablets" win.
// They stay in the Workbox precache (not globIgnored), so on-floor navigation
// is still instant/offline; only the shell parse cost + per-deploy delta shrink.
const PalletsPage            = lazyWithReload(() => import('@/modules/packhouse/Pallets'))
const QCStage                = lazyWithReload(() => import('@/modules/packhouse/stages/QCStage'))
const DispatchPage           = lazyWithReload(() => import('@/modules/packhouse/Dispatch'))
const TruckPlannerPage       = lazyWithReload(() => import('@/modules/packhouse/TruckPlanner'))
const LoadingBayPage         = lazyWithReload(() => import('@/modules/packhouse/LoadingBay'))
const ReceiptsPage           = lazyWithReload(() => import('@/modules/packhouse/Receipts'))
const PalletDetailPage       = lazyWithReload(() => import('@/modules/shared/PalletDetail'))
const RejectBinsPage         = lazyWithReload(() => import('@/modules/packhouse/RejectBins').then(m => ({ default: m.default })))
const RejectBinDetailPage    = lazyWithReload(() => import('@/modules/packhouse/RejectBins').then(m => ({ default: m.RejectBinDetailPage })))
const RejectDispatchPage     = lazyWithReload(() => import('@/modules/packhouse/RejectDispatch'))
const RejectDispatchPrintPage = lazyWithReload(() => import('@/modules/packhouse/RejectDispatchPrint'))
const RejectDispatchesReportPage = lazyWithReload(() => import('@/modules/packhouse/RejectDispatchesReport'))
const RejectDispatchReceiptPage  = lazyWithReload(() => import('@/modules/packhouse/RejectDispatchReceipt'))
const DeliveryDetailPage     = lazyWithReload(() => import('@/modules/packhouse/DeliveryDetail'))
const DeliveryNotePrintPage  = lazyWithReload(() => import('@/modules/packhouse/DeliveryNotePrint'))
const MissionDetailPage      = lazyWithReload(() => import('@/modules/packhouse/MissionDetail'))
const PackRefCodesPage       = lazyWithReload(() => import('@/modules/packhouse/PackRefCodes'))
const PackInstructionsPage   = lazyWithReload(() => import('@/modules/packhouse/orders/PackInstructions'))
const PackInstructionDetailPage = lazyWithReload(() => import('@/modules/packhouse/orders/PackInstructionDetail'))
const PackInstructionPrintPage  = lazyWithReload(() => import('@/modules/packhouse/orders/PackInstructionPrint'))
const OrdersPage             = lazyWithReload(() => import('@/modules/packhouse/orders/Orders'))
const OrderDetailPage        = lazyWithReload(() => import('@/modules/packhouse/orders/OrderDetail'))
const ProductionSheetPage    = lazyWithReload(() => import('@/modules/packhouse/orders/ProductionSheetPrint'))
const OrderPrintPage         = lazyWithReload(() => import('@/modules/packhouse/orders/OrderPrint'))
const ProductsPage           = lazyWithReload(() => import('@/modules/packhouse/orders/Products'))
const RunDetailPage          = lazyWithReload(() => import('@/modules/packhouse/RunDetail'))
const ConsignmentsPage       = lazyWithReload(() => import('@/modules/packhouse/Consignments'))
const PpecbCertificatesPage  = lazyWithReload(() => import('@/modules/packhouse/PpecbCertificates'))
const RebinPage              = lazyWithReload(() => import('@/modules/packhouse/Rebin'))
const YardPage               = lazyWithReload(() => import('@/modules/packhouse/Yard'))
const BinInventoryPage       = lazyWithReload(() => import('@/modules/packhouse/bin-inventory'))
const PrecoolStage           = lazyWithReload(() => import('@/modules/packhouse/stages/PrecoolStage'))
const PrecoolReportPage      = lazyWithReload(() => import('@/modules/packhouse/PrecoolReport'))
const ColdroomReportPage     = lazyWithReload(() => import('@/modules/packhouse/ColdroomReport'))
const ColdroomTemperatureLogPage = lazyWithReload(() => import('@/modules/packhouse/ColdroomTemperatureLog'))
const ColdTreatmentLogPage = lazyWithReload(() => import('@/modules/packhouse/ColdTreatmentLog'))
const ConfigPage             = lazyWithReload(() => import('@/modules/packhouse/config'))
const FinancialPage          = lazyWithReload(() => import('@/modules/finance/financial'))
const MarketPricesPage       = lazyWithReload(() => import('@/modules/shared/MarketPrices'))
const InvoicePage            = lazyWithReload(() => import('@/modules/finance/Invoice'))
const GrowerStatementPage    = lazyWithReload(() => import('@/modules/finance/GrowerStatement'))
const GrowerStatementRenderPage = lazyWithReload(() => import('@/modules/finance/GrowerStatementRender'))
import RenderRoute from '@/components/RenderRoute'
const GrowerSeasonSummaryPage = lazyWithReload(() => import('@/modules/finance/GrowerSeasonSummary'))
const GrowersSeasonSummaryPage = lazyWithReload(() => import('@/modules/finance/GrowersSeasonSummary'))
const TransportInvoicePage   = lazyWithReload(() => import('@/modules/finance/TransportInvoice'))
const TransportReconPage     = lazyWithReload(() => import('@/modules/finance/TransportRecon'))
const UploadCostingsPage     = lazyWithReload(() => import('@/modules/finance/UploadCostings'))
const LabourPage             = lazyWithReload(() => import('@/modules/packhouse/Labour'))
const PackhouseTeamsPage     = lazyWithReload(() => import('@/modules/packhouse/Teams'))
const TeamRollCallPage       = lazyWithReload(() => import('@/modules/packhouse/TeamRollCall'))
const PackhouseTeamPerformancePage = lazyWithReload(() => import('@/modules/packhouse/TeamPerformance'))
const PackhousePieceworkConfigPage = lazyWithReload(() => import('@/modules/packhouse/PieceworkConfig'))
const PackerCapturePage           = lazyWithReload(() => import('@/modules/packhouse/PackerCapture'))
const PieceBagReportPage          = lazyWithReload(() => import('@/modules/packhouse/PieceBagReport'))
const ClockFacePage          = lazyWithReload(() => import('@/modules/shared/ClockFace'))
const DeviceLockScreenPage   = lazyWithReload(() => import('@/modules/shared/DeviceLockScreen'))
const DeviceCaptureScreenPage = lazyWithReload(() => import('@/modules/shared/DeviceCaptureScreen'))
const FaceMatchLogPage       = lazyWithReload(() => import('@/modules/shared/labour/FaceMatchLog'))
const LabourConfigPage       = lazyWithReload(() => import('@/modules/shared/labour/LabourConfig'))
const PayslipPage            = lazyWithReload(() => import('@/modules/shared/labour/Payslip'))
const PayslipsBulkPage       = lazyWithReload(() => import('@/modules/shared/labour/PayslipsBulk'))
const AttendanceBulkPage     = lazyWithReload(() => import('@/modules/shared/labour/AttendancePieceworkBulk'))
const AttendanceReportPage   = lazyWithReload(() => import('@/modules/shared/labour/AttendancePieceworkReport'))
const EmployeeDayReportPage  = lazyWithReload(() => import('@/modules/shared/labour/EmployeeDayReport'))
const ActivitiesReportPage   = lazyWithReload(() => import('@/modules/shared/labour/tabs/activities/ActivitiesReportPrint'))
const TerminationReportPage  = lazyWithReload(() => import('@/modules/shared/labour/TerminationReports').then(m => ({ default: m.TerminationReportPage })))
const EeReportPage = lazyWithReload(() => import('@/modules/shared/labour/EeReport').then(m => ({ default: m.EeReportPage })))
const TerminationLeavePayPage = lazyWithReload(() => import('@/modules/shared/labour/TerminationReports').then(m => ({ default: m.TerminationLeavePayPage })))
const RollUtilisationPage    = lazyWithReload(() => import('@/modules/shared/labour/RollUtilisation'))
const PieceworkReportPage    = lazyWithReload(() => import('@/modules/shared/labour/PieceworkTallyReport'))
const PayrollRegisterPage    = lazyWithReload(() => import('@/modules/shared/labour/tabs/payroll/PayrollRegister'))
const Emp201ReconPage        = lazyWithReload(() => import('@/modules/shared/labour/tabs/payroll/Emp201Recon'))
const WorkerEarningsPage     = lazyWithReload(() => import('@/modules/shared/labour/WorkerEarnings'))
const PackerPerformancePage  = lazyWithReload(() => import('@/modules/shared/labour/PackerPerformance'))
const ParticularsPrintPage   = lazyWithReload(() => import('@/modules/shared/labour/ParticularsPrint'))
const ContractPrintPage      = lazyWithReload(() => import('@/modules/shared/labour/ContractPrint'))
const CertificateOfServicePage = lazyWithReload(() => import('@/modules/shared/labour/CertificateOfServicePrint'))
const Ui27SheetPage = lazyWithReload(() => import('@/modules/shared/labour/Ui27SheetPrint'))
const InjuryWcl2Page = lazyWithReload(() => import('@/modules/shared/labour/injuries/InjuryWcl2Print'))
const WarningLetterPrintPage = lazyWithReload(() => import('@/modules/shared/labour/WarningLetterPrint'))
const WorkerPortalPage       = lazyWithReload(() => import('@/modules/shared/labour/WorkerPortal'))
const PayslipSharePage       = lazyWithReload(() => import('@/modules/finance/PayslipShare'))
const BuyerTrackPage         = lazyWithReload(() => import('@/modules/exporter/BuyerTrack'))
const BuyerPortalPage        = lazyWithReload(() => import('@/modules/exporter/BuyerPortal'))
const BuyerDashboardPage     = lazyWithReload(() => import('@/modules/exporter/BuyerDashboard'))
const BuyerMarketPage        = lazyWithReload(() => import('@/modules/buyer/BuyerMarket'))
const ExporterTradeSalesPage = lazyWithReload(() => import('@/modules/exporter/ExporterTradeSales'))
const StockPage              = lazyWithReload(() => import('@/modules/packhouse/Stock'))
const SecurityIncidentsPage  = lazyWithReload(() => import('@/modules/shared/SecurityIncidents'))
const SecurityIncidentPrintPage = lazyWithReload(() => import('@/modules/shared/SecurityIncidentPrint'))
const PurchaseOrdersPage     = lazyWithReload(() => import('@/modules/packhouse/purchasing/PurchaseOrders'))
const PurchaseOrderDetailPage = lazyWithReload(() => import('@/modules/packhouse/purchasing/PurchaseOrderDetail'))
const PurchaseOrderPrintPage = lazyWithReload(() => import('@/modules/packhouse/purchasing/PurchaseOrderPrint'))
const PurchaseChecksPage     = lazyWithReload(() => import('@/modules/packhouse/purchasing/PurchaseChecks'))
const QuoteBuilderPage       = lazyWithReload(() => import('@/modules/finance/quotes/QuoteBuilder'))
const QuotePrintPage         = lazyWithReload(() => import('@/modules/finance/quotes/QuotePrint'))
const SecurityCheckpointTagsPage = lazyWithReload(() => import('@/modules/shared/SecurityCheckpointTags'))
const CompliancePage         = lazyWithReload(() => import('@/modules/shared/compliance/Compliance'))
const ComplianceAuditPackPage = lazyWithReload(() => import('@/modules/shared/compliance/ComplianceAuditPack'))
const MyChecklistsPage       = lazyWithReload(() => import('@/modules/shared/MyChecklists'))
const EstimatesPage          = lazyWithReload(() => import('@/modules/packhouse/Estimates'))
const EstimatesReportPage    = lazyWithReload(() => import('@/modules/packhouse/EstimatesReport'))
const UsersPage              = lazyWithReload(() => import('@/modules/shared/Users'))
const AddonsPage             = lazyWithReload(() => import('@/modules/shared/Addons'))
const BillingPage            = lazyWithReload(() => import('@/modules/shared/Billing'))
const SaasInvoicePrintPage   = lazyWithReload(() => import('@/modules/shared/SaasInvoicePrint'))
const SaasStatementPrintPage = lazyWithReload(() => import('@/modules/platform_admin/saas_billing/SaasStatementPrint'))
const PackersPage            = lazyWithReload(() => import('@/modules/packhouse/Packers'))
const ActivityPage           = lazyWithReload(() => import('@/modules/platform_admin/Activity'))
const TenantAuditPage         = lazyWithReload(() => import('@/modules/platform_admin/TenantAudit'))
const MovedToAdminAppPage     = lazyWithReload(() => import('@/modules/platform_admin/MovedToAdminApp'))
const SupportViewStartPage    = lazyWithReload(() => import('@/modules/shared/SupportViewStart'))
const BenchmarksPage          = lazyWithReload(() => import('@/modules/shared/Benchmarks'))
const MaintenancePage        = lazyWithReload(() => import('@/modules/packhouse/maintenance/Maintenance'))
const WorkOrderDetailPage    = lazyWithReload(() => import('@/modules/packhouse/maintenance/WorkOrderDetail'))
const AssetDetailPage        = lazyWithReload(() => import('@/modules/packhouse/maintenance/AssetDetail'))
const MaintenanceReportPage  = lazyWithReload(() => import('@/modules/packhouse/maintenance/MaintenanceReport'))
const FloorMonitorPage       = lazyWithReload(() => import('@/modules/packhouse/FloorMonitor'))
const AskFloorPage           = lazyWithReload(() => import('@/modules/packhouse/AskFloor'))
const RecommendationsPage    = lazyWithReload(() => import('@/modules/packhouse/Recommendations'))
const CalendarPage           = lazyWithReload(() => import('@/modules/packhouse/calendar'))
const PackSchedulePage       = lazyWithReload(() => import('@/modules/packhouse/pack-schedule/PackSchedulePage'))
const PackProgrammePage      = lazyWithReload(() => import('@/modules/packhouse/pack-programme/PackProgrammePage'))
// Line Layout — the packhouse floor to scale, mig 20261054600000.
const LineLayoutPage         = lazyWithReload(() => import('@/modules/packhouse/lineLayout/LineLayout'))
const PackProgrammePrintPage = lazyWithReload(() => import('@/modules/packhouse/pack-programme/PackProgrammePrint'))
const PackSheetReadPage = lazyWithReload(() => import('@/modules/packhouse/orders/PackSheetRead'))
const DeliveriesWeeklyPage   = lazyWithReload(() => import('@/modules/packhouse/DeliveriesWeekly'))
const DeliveriesReportStage  = lazyWithReload(() => import('@/modules/packhouse/stages/DeliveriesReportStage'))
const CrateWeightsPage       = lazyWithReload(() => import('@/modules/packhouse/CrateWeights'))
const CultivarReportPage     = lazyWithReload(() => import('@/modules/packhouse/CultivarReport'))
const ReceivePalletsPage     = lazyWithReload(() => import('@/modules/packhouse/ReceivePallets'))
const ReceiveArrivalPage     = lazyWithReload(() => import('@/modules/packhouse/ReceiveArrival'))
const PalletReceiptPage      = lazyWithReload(() => import('@/modules/packhouse/ReceiptDetail'))
const PalletReceiptReportPage = lazyWithReload(() => import('@/modules/packhouse/PalletReceiptReport'))
const ReceiptPackoutReportPage = lazyWithReload(() => import('@/modules/packhouse/ReceiptPackoutReport'))
const GrowersPage            = lazyWithReload(() => import('@/modules/packhouse/Growers'))
const GrowerDetailPage       = lazyWithReload(() => import('@/modules/shared/GrowerDetail'))
const PackingContractsPage   = lazyWithReload(() => import('@/modules/packhouse/contracts/PackingContracts'))
const PackingContractPage    = lazyWithReload(() => import('@/modules/packhouse/contracts/PackingContract'))
const PackingContractTemplatePage = lazyWithReload(() => import('@/modules/packhouse/contracts/PackingContractTemplate'))
const PackingContractSignPage = lazyWithReload(() => import('@/modules/grower_portal/PackingContractSign'))
const GrowerReportPage       = lazyWithReload(() => import('@/modules/packhouse/GrowerReport'))
const GrowersOverallReportPage = lazyWithReload(() => import('@/modules/packhouse/GrowersOverallReport'))
const OrchardCensusPage      = lazyWithReload(() => import('@/modules/packhouse/OrchardCensus'))
const QCReportPage           = lazyWithReload(() => import('@/modules/packhouse/QCReport'))
const QcMultiRunReportPage   = lazyWithReload(() => import('@/modules/packhouse/QcMultiRunReport'))
const StockReportPage        = lazyWithReload(() => import('@/modules/packhouse/StockReport'))
const StockCategoriesPage    = lazyWithReload(() => import('@/modules/packhouse/StockCategoriesAdmin'))
const StockCountsPage        = lazyWithReload(() => import('@/modules/packhouse/StockCounts'))
const StockCountSheetPage    = lazyWithReload(() => import('@/modules/packhouse/StockCountSheet'))
const StockRecallPage        = lazyWithReload(() => import('@/modules/packhouse/StockRecall'))
const StockLocationsPage     = lazyWithReload(() => import('@/modules/packhouse/StockLocationsAdmin'))
const StockOwnersPage        = lazyWithReload(() => import('@/modules/shared/StockOwnersAdmin'))
const StockScanPage          = lazyWithReload(() => import('@/modules/packhouse/StockScan'))
const StockLabelsPage        = lazyWithReload(() => import('@/modules/packhouse/StockLabels'))
const SuppliersPage          = lazyWithReload(() => import('@/modules/packhouse/Suppliers'))
const BinHistoryReportPage   = lazyWithReload(() => import('@/modules/packhouse/BinHistoryReport'))
const CagesReportPage        = lazyWithReload(() => import('@/modules/packhouse/CagesReport'))
const TraceReportPage        = lazyWithReload(() => import('@/modules/packhouse/TraceReport'))
const UncodedPalletsPage     = lazyWithReload(() => import('@/modules/packhouse/UncodedPallets'))
const ProofOfSubmissionPage  = lazyWithReload(() => import('@/modules/packhouse/ProofOfSubmission'))
const ReconciliationPage     = lazyWithReload(() => import('@/modules/packhouse/Reconciliation'))
const RateCardPage           = lazyWithReload(() => import('@/modules/finance/RateCard'))
const CommoditiesAdminPage   = lazyWithReload(() => import('@/modules/packhouse/CommoditiesAdmin'))
const NotificationsPage      = lazyWithReload(() => import('@/modules/shared/Notifications'))
const NotificationSettingsPage = lazyWithReload(() => import('@/modules/shared/NotificationSettings'))
const TrainingPage           = lazyWithReload(() => import('@/modules/shared/training/Training'))
const TrainingGuidePage      = lazyWithReload(() => import('@/modules/shared/training/TrainingGuide'))
const RecallPage             = lazyWithReload(() => import('@/modules/shared/Recall'))
const RecallDayPage          = lazyWithReload(() => import('@/modules/shared/RecallDay'))
const UploadDiagnosticPage   = lazyWithReload(() => import('@/modules/platform_admin/UploadDiagnostic'))
const PrivacyPolicyPage      = lazyWithReload(() => import('@/modules/shared/PrivacyPolicy'))
const TermsOfServicePage     = lazyWithReload(() => import('@/modules/shared/TermsOfService'))
const SubjectAccessRequestPage = lazyWithReload(() => import('@/modules/shared/SubjectAccessRequest'))
const OAuthConsentPage       = lazyWithReload(() => import('@/modules/shared/OAuthConsent'))
const OpenSourceLicencesPage = lazyWithReload(() => import('@/modules/shared/OpenSourceLicences'))

// Farm-tenant pages — never rendered for a KF49 packhouse operator
// (gFarm gate blocks the route). Lazy so they don't bloat the main
// bundle for the packhouse audience; FARM-tenant users still get a
// fast first paint because chunk fetch is small.
const SpraysPage                       = lazyWithReload(() => import('@/modules/farm/Sprays'))
const FarmWeatherPage                  = lazyWithReload(() => import('@/modules/farm/FarmWeather'))
const FertApplicationsPage             = lazyWithReload(() => import('@/modules/farm/FertApplications'))
const PhenologyPage                    = lazyWithReload(() => import('@/modules/farm/Phenology'))
const FarmLabourPage                   = lazyWithReload(() => import('@/modules/farm/FarmLabour'))
const FarmEstimatesPage                = lazyWithReload(() => import('@/modules/farm/FarmEstimates'))
const FarmEquipmentPage                = lazyWithReload(() => import('@/modules/farm/FarmEquipment'))
const FarmCompliancePage               = lazyWithReload(() => import('@/modules/farm/FarmCompliance'))
const SizaAssessmentPage               = lazyWithReload(() => import('@/modules/farm/SizaAssessment'))
const FarmComplianceBlockReportPage    = lazyWithReload(() => import('@/modules/farm/FarmComplianceBlockReport'))
const FarmComplianceDocsPage           = lazyWithReload(() => import('@/modules/farm/FarmComplianceDocs'))
const HarvestPicksPage                 = lazyWithReload(() => import('@/modules/farm/HarvestPicks'))
const FarmLoadsPage                    = lazyWithReload(() => import('@/modules/farm/FarmLoads'))
const OrchardPage                      = lazyWithReload(() => import('@/modules/farm/Orchard'))
const ScoutingPage                     = lazyWithReload(() => import('@/modules/farm/Scouting'))
const FarmProblemsPage                 = lazyWithReload(() => import('@/modules/farm/FarmProblems'))
const FarmTrapsPage                    = lazyWithReload(() => import('@/modules/farm/FarmTraps'))
const PackhouseReportIncidentPage      = lazyWithReload(() => import('@/modules/packhouse/PackhouseReportIncident'))
const FarmReportIncidentPage           = lazyWithReload(() => import('@/modules/farm/FarmReportIncident'))
// Organogram — one page for four apps (packhouse + BigPayroll, farm, BigExport), mig 20261050800000.
const OrganogramPage                   = lazyWithReload(() => import('@/modules/shared/organogram/Organogram'))
const FarmCalendarPage                 = lazyWithReload(() => import('@/modules/farm/FarmCalendar'))
const ScoutingMapPage                  = lazyWithReload(() => import('@/modules/farm/ScoutingMap'))
const SprayMapPage                     = lazyWithReload(() => import('@/modules/farm/SprayMap'))
const FarmMembersPage                  = lazyWithReload(() => import('@/modules/farm/FarmMembers'))
const FarmConfigPage                   = lazyWithReload(() => import('@/modules/farm/FarmConfig'))
const FarmCommoditiesPage              = lazyWithReload(() => import('@/modules/farm/FarmCommodities'))
const FarmSettingsPage                 = lazyWithReload(() => import('@/modules/farm/FarmSettings'))
const FarmTeamsPage                    = lazyWithReload(() => import('@/modules/farm/FarmTeams'))
const PieceworkConfigPage              = lazyWithReload(() => import('@/modules/farm/PieceworkConfig'))
const FarmTeamPerformancePage          = lazyWithReload(() => import('@/modules/farm/FarmTeamPerformance'))
const ScoutTargetsPage                 = lazyWithReload(() => import('@/modules/farm/ScoutTargets'))
const SprayRecipesPage                 = lazyWithReload(() => import('@/modules/farm/SprayRecipes'))
const FarmMapPage                      = lazyWithReload(() => import('@/modules/farm/FarmMap'))
const FarmCostReportPage               = lazyWithReload(() => import('@/modules/farm/FarmCostReport'))
const FarmBudgetPage                   = lazyWithReload(() => import('@/modules/farm/FarmBudget'))
const FarmRiskProfilePage              = lazyWithReload(() => import('@/modules/farm/FarmRiskProfile'))
const WaterSchedulePage                = lazyWithReload(() => import('@/modules/farm/WaterSchedule'))
const DrainMonitoringPage              = lazyWithReload(() => import('@/modules/farm/DrainMonitoring'))
const DrainReportPage                  = lazyWithReload(() => import('@/modules/farm/DrainReport'))
const PulseReportPage                  = lazyWithReload(() => import('@/modules/farm/PulseReport'))
const FarmAnalysesPage                 = lazyWithReload(() => import('@/modules/farm/FarmAnalyses'))
const FarmActivityPage                 = lazyWithReload(() => import('@/modules/farm/FarmActivity'))
const FarmReceiptsPage                 = lazyWithReload(() => import('@/modules/farm/FarmReceipts'))
const FarmSuppliersPage                = lazyWithReload(() => import('@/modules/farm/FarmSuppliers'))
const ChemicalsPage                    = lazyWithReload(() => import('@/modules/farm/Chemicals'))
const FarmStockPage                    = lazyWithReload(() => import('@/modules/farm/FarmStock'))
const FarmPpePage                      = lazyWithReload(() => import('@/modules/farm/FarmPpe'))
const FarmStockCategoriesPage          = lazyWithReload(() => import('@/modules/farm/FarmStockCategoriesAdmin'))
const VehicleLogbookPage               = lazyWithReload(() => import('@/modules/farm/VehicleLogbook'))
const FuelPage                         = lazyWithReload(() => import('@/modules/farm/Fuel'))
const FuelReportPage                   = lazyWithReload(() => import('@/modules/farm/FuelReport'))
const FarmUtilitiesPage                = lazyWithReload(() => import('@/modules/farm/FarmUtilities'))
const FarmFieldOpsPage                 = lazyWithReload(() => import('@/modules/farm/FarmFieldOps'))
const FarmSeedsPage                    = lazyWithReload(() => import('@/modules/farm/FarmSeeds'))

// Agent-tenant pages — same logic. KF49 is a PACKHOUSE tenant; never
// hits /agent. AGENT-tenant users see one small chunk per page they
// visit instead of carrying the whole thing in the main bundle.
const AgentVisitsPage         = lazyWithReload(() => import('@/modules/agent/AgentVisits'))
const AgentVisitDetailPage    = lazyWithReload(() => import('@/modules/agent/AgentVisitDetail'))
const AgentProductsPage       = lazyWithReload(() => import('@/modules/agent/AgentProducts'))
const AgentRecipesPage        = lazyWithReload(() => import('@/modules/agent/AgentRecipes'))
const AgentConfigPage         = lazyWithReload(() => import('@/modules/agent/AgentConfig'))
const SupplierProductsPage    = lazyWithReload(() => import('@/modules/supplier/SupplierProducts'))
const SupplierDeliveriesPage  = lazyWithReload(() => import('@/modules/supplier/SupplierDeliveries'))
const SupplierMaintReqPage    = lazyWithReload(() => import('@/modules/supplier/SupplierMaintenanceRequests'))
const SupplierCustomersPage   = lazyWithReload(() => import('@/modules/supplier/SupplierCustomers'))
const SupplierConfigPage      = lazyWithReload(() => import('@/modules/supplier/SupplierConfig'))
// Transporter app (BigLogistics P0) — TRANSPORTER-kind tenants.
const TransporterPartnersPage = lazyWithReload(() => import('@/modules/transporter/TransporterPartners'))
const SaasBillingPage         = lazyWithReload(() => import('@/modules/platform_admin/saas_billing/SaasBilling'))
const TransporterTripsPage    = lazyWithReload(() => import('@/modules/transporter/TransporterTrips'))
const TransporterFleetPage    = lazyWithReload(() => import('@/modules/transporter/TransporterFleet'))
const TransporterAvailabilityPage = lazyWithReload(() => import('@/modules/transporter/TransporterAvailability'))
const TransporterConfigPage   = lazyWithReload(() => import('@/modules/transporter/TransporterConfig'))
const HarvestClientsPage      = lazyWithReload(() => import('@/modules/harvest/HarvestClients'))
const HarvestConfigPage       = lazyWithReload(() => import('@/modules/harvest/HarvestConfig'))
const SecuritySitesPage       = lazyWithReload(() => import('@/modules/security/SecuritySites'))
const SecurityConfigPage      = lazyWithReload(() => import('@/modules/security/SecurityConfig'))
const SecurityIncidentRegisterPage = lazyWithReload(() => import('@/modules/security/SecurityIncidentRegister'))
const HarvestSpansPage        = lazyWithReload(() => import('@/modules/harvest/HarvestSpans'))
const HarvestCapturePage      = lazyWithReload(() => import('@/modules/harvest/HarvestCapture'))
const HarvestDayEndPage       = lazyWithReload(() => import('@/modules/harvest/HarvestDayEnd'))
const HarvestMonitorPage      = lazyWithReload(() => import('@/modules/harvest/HarvestMonitor'))
const HarvestPickingMapPage   = lazyWithReload(() => import('@/modules/harvest/HarvestPickingMap'))
const HarvestWorkersPage      = lazyWithReload(() => import('@/modules/harvest/HarvestWorkers'))
const HarvestRatesPage        = lazyWithReload(() => import('@/modules/harvest/HarvestRates'))
const HarvestDestinationsPage = lazyWithReload(() => import('@/modules/harvest/HarvestDestinations'))
const HarvestTasksPage       = lazyWithReload(() => import('@/modules/harvest/HarvestTasks'))

// Exporter app (BigExport) — EXPORTER-kind tenants only.
const ExporterLoadsPage        = lazyWithReload(() => import('@/modules/exporter/ExporterLoads'))
const ExporterStockPage        = lazyWithReload(() => import('@/modules/exporter/ExporterStock'))
const ExporterPackhousePalletsPage = lazyWithReload(() => import('@/modules/exporter/ExporterPackhousePallets'))
const ExporterPartnerStockPage = lazyWithReload(() => import('@/modules/exporter/ExporterPartnerStock'))
const TradeListingsPage = lazyWithReload(() => import('@/modules/packhouse/TradeListings'))
const ExporterPackingListsPage = lazyWithReload(() => import('@/modules/exporter/ExporterPackingLists'))
const ExporterPackoutsPage     = lazyWithReload(() => import('@/modules/exporter/ExporterPackouts'))
const ExporterInvoicesPage     = lazyWithReload(() => import('@/modules/exporter/ExporterInvoices'))
const ExporterSalesOrdersPage  = lazyWithReload(() => import('@/modules/exporter/ExporterSalesOrders'))
const ExporterConfigPage       = lazyWithReload(() => import('@/modules/exporter/ExporterConfig'))
const ExporterReceiversPage    = lazyWithReload(() => import('@/modules/exporter/ExporterReceivers'))
const ExporterReceivablesPage  = lazyWithReload(() => import('@/modules/exporter/ExporterReceivables'))
const ExporterStatementPage    = lazyWithReload(() => import('@/modules/exporter/ExporterStatement'))
const ExporterShippingAgentsPage = lazyWithReload(() => import('@/modules/exporter/ExporterShippingAgents'))
const ExporterMembersPage      = lazyWithReload(() => import('@/modules/exporter/ExporterMembers'))
const ExporterTrackingPage     = lazyWithReload(() => import('@/modules/exporter/ExporterTracking'))
import { RailShell } from '@/components/ModuleRail'
import { useCanOpenPieceRates } from '@/hooks/useCanOpenPieceRates'
import { useCanOpenFarmPieceRates } from '@/hooks/useCanOpenFarmPieceRates'
const ExporterOverviewPage       = lazyWithReload(() => import('@/modules/exporter/ExporterOverview'))
const ExporterShipmentsPage      = lazyWithReload(() => import('@/modules/exporter/ExporterShipments'))
const ExporterShipmentDetailPage = lazyWithReload(() => import('@/modules/exporter/ExporterShipmentDetail'))
const ExporterActivityPage       = lazyWithReload(() => import('@/modules/exporter/ExporterActivity'))

// Franchise admin pages — only reachable by a FRANCHISE_ADMIN-roled user
// inside an AGENT tenant that carries a franchise_id. gFranchise gate
// below enforces all three. Chunk lazy-loads on first navigation.
const FranchiseAdminPage      = lazyWithReload(() => import('@/modules/franchise/FranchiseAdmin'))
const FranchiseChemicalsPage  = lazyWithReload(() => import('@/modules/franchise/FranchiseChemicals'))
const FranchiseAgentDetailPage = lazyWithReload(() => import('@/modules/franchise/FranchiseAgentDetail'))

// Public grower-portal — anon-token routes, only hit when a grower
// follows the link in their share-token URL. Most operators never
// touch these. Eager-loading them for everyone is wasted bytes.
const GrowerPortalPage     = lazyWithReload(() => import('@/modules/grower_portal/GrowerPortal'))
const GrowerPortalHomePage = lazyWithReload(() => import('@/modules/grower_portal/GrowerPortalHome'))

// ONE route for the two anon portals that share the /portal/<something> shape.
// Both generators mint that shape — Growers.tsx:1342 `/portal/<growerId>?t=<token>`
// and ExporterReceivers.tsx:344 `/portal/<token>` — so declaring them as two
// sibling <Route>s was a collision: react-router scores `/portal/:growerId` and
// `/portal/:token` identically and breaks the tie by declaration order. The
// anon block listed the grower portal first, so every buyer opening their
// minted link rendered GrowerPortalHome, which short-circuits to "Invalid link"
// with no ?t= — the buyer portal was dead for its only audience — while the
// authed block, ordered the other way, sent a manager previewing a grower link
// to BuyerPortal. Dispatch on the ?t= param instead: grower links always carry
// it, buyer links never do, so every URL already issued to a grower or a
// receiver keeps resolving to the page it was minted for.
function PortalRoute() {
  const [portalParams] = useSearchParams()
  return portalParams.get('t') ? <GrowerPortalHomePage /> : <BuyerPortalPage />
}

// Printable + deep-detail pages — operator opens occasionally from a
// list view. Chunk fetch lands in a couple of hundred ms; that's an
// acceptable trade for slimming the main bundle.
const DispatchNotePage     = lazyWithReload(() => import('@/modules/packhouse/DispatchNote'))
const ConsignmentNotePage  = lazyWithReload(() => import('@/modules/packhouse/ConsignmentNote'))
const BinLabelPage         = lazyWithReload(() => import('@/modules/packhouse/BinLabel'))
const BinLabelsPage        = lazyWithReload(() => import('@/modules/packhouse/BinLabels'))
const BatchLabelPage       = lazyWithReload(() => import('@/modules/packhouse/BatchLabel'))
const DeliveryLabelPage    = lazyWithReload(() => import('@/modules/packhouse/DeliveryLabel'))
const LabelPrintPage       = lazyWithReload(() => import('@/modules/packhouse/LabelPrint'))
const PrintingSetupPage    = lazyWithReload(() => import('@/modules/packhouse/PrintingSetup'))
const LabelDesignerPage    = lazyWithReload(() => import('@/modules/packhouse/LabelDesigner'))
const BinDetailPage        = lazyWithReload(() => import('@/modules/packhouse/BinDetail'))
const SprayNotePage        = lazyWithReload(() => import('@/modules/farm/SprayNote'))
const SprayReportPage      = lazyWithReload(() => import('@/modules/farm/SprayReport'))
const SprayDrivingPage     = lazyWithReload(() => import('@/modules/farm/SprayDriving'))
const HarvestPicksReportPage = lazyWithReload(() => import('@/modules/farm/HarvestPicksReport'))
const FertReportPage       = lazyWithReload(() => import('@/modules/farm/FertReport'))
const ScoutingTrendsPage   = lazyWithReload(() => import('@/modules/farm/ScoutingTrends'))
const ScoutReportPage      = lazyWithReload(() => import('@/modules/farm/ScoutReport'))
const ScoutSummaryPage     = lazyWithReload(() => import('@/modules/farm/ScoutSummary'))
const PhenologyReportPage  = lazyWithReload(() => import('@/modules/farm/PhenologyReport'))

// The B loader shown while a lazy chunk is downloading. Same mark as the
// session-loading one below for visual consistency.
const PageFallback = (
  <div className="h-full flex flex-col items-center justify-center gap-3">
    <BrandLoader />
    <LoadingWords />
  </div>
)

// ScrollToTop — react-router preserves the previous routes scroll position
// across navigations, which makes pages open partway-down whenever the
// previous page was scrolled. Resets on every pathname change. Walks any
// .overflow-y-auto containers because most pages own their own scroll
// region (min-h-full + flex-1 + overflow-y-auto pattern) instead of using
// window scroll.
// The saved Dark / Light choice follows the route: an app page may be light, the
// clock face and paired-device screens never are, and a client-side move between
// them (no reload) must flip the page with it. Mounted in the signed-in tree; the
// signed-out kiosk screens load fresh, so the index.html boot script covers them.
function AppearanceOnNavigate() {
  const { pathname } = useLocation()
  useLayoutEffect(() => { applyAppearance() }, [pathname])
  return null
}

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    // Wait for the new page to commit before resetting — otherwise the
    // scroll set runs against the previous DOM and the new mount restores
    // its own state on top of us.
    const id = requestAnimationFrame(() => {
      window.scrollTo(0, 0)
      document.querySelectorAll<HTMLElement>('.overflow-y-auto, .overflow-auto')
        .forEach(el => { el.scrollTop = 0 })
    })
    return () => cancelAnimationFrame(id)
  }, [pathname])
  return null
}

// UiTelemetryRouteListener — anonymous page-view + dwell tracking (mig 805,
// see src/lib/uiTelemetry.ts for the privacy contract). Mounted ONLY in the
// authenticated branch below, so pre-auth routes (/login, /clock-face, share
// links) are never observed.
function UiTelemetryRouteListener() {
  const { pathname } = useLocation()
  useEffect(() => { trackRouteChange(pathname) }, [pathname])
  return null
}

// RequireModule — route guard. Reads the current user + active tenant
// from cached queries. While loading we render nothing (split-second on
// a warm cache); once loaded, redirect to / if either gate denies access.
//
// Two gates (see hasModule docs):
//   1. Tenant entitlement cap (billing-level subscription)
//   2. User permission (admin bypass, role defaults, explicit list)
//
// Admins bypass the user gate but NOT the tenant cap — a tenant that's
// not paying for the Payroll module has no admin who can sneak in.
function RequireModule({ moduleKey, anyOf, children }: { moduleKey: string; anyOf?: readonly string[]; children: ReactNode }) {
  // isPending, NOT isLoading (finding H4, 2026-06-11): in react-query v5
  // isLoading = isPending && isFetching, which is FALSE while
  // PersistQueryClientProvider restores the boot cache (queries pending but
  // not yet fetching) — so a cold boot on a deep route read "not loading +
  // no data" as a denial and bounced to "/" before the queries ever ran
  // (the unexplained 2026-06-05 /consignment-note bounce). The rule is
  // "no data yet = still deciding, render null"; e2e/stuck-loading.spec.ts
  // locks this in.
  const { data: me, isPending: mePending } = useCurrentUser()
  const { data: tenant, isPending: tenantPending } = useActiveTenant()
  // Custom-role holders (mig 20260706150000) resolve module access from
  // their permission map. Same isPending rule as me/tenant: no data yet =
  // still deciding, render null — a custom-role user judged by the legacy
  // path before the map arrives would bounce off their own routes.
  const { perms, isPending: permsPending } = usePermissions()
  if (mePending || tenantPending || permsPending) return null
  if (!moduleRouteAllowed(moduleKey, anyOf, me, tenant, perms)) {
    logModuleDenied(moduleKey, me, tenant)
    return <Navigate to="/" replace />
  }
  return <MoneyGate moduleKey={moduleKey}>{children}</MoneyGate>
}

type GateMe = ReturnType<typeof useCurrentUser>['data']
type GateTenant = ReturnType<typeof useActiveTenant>['data']

/** RequireModule's answer. anyOf: the page opens to a holder of ANY of those
 *  keys (BIN_PAGE_MODULES); moduleKey still names the money gate and the log line. */
function moduleRouteAllowed(
  moduleKey: string, anyOf: readonly string[] | undefined,
  me: GateMe, tenant: GateTenant, perms: ReturnType<typeof usePermissions>['perms'],
): boolean {
  const enabled = tenant?.enabledModules ?? null
  return anyOf
    ? hasAnyModule(me ?? null, anyOf, enabled, perms, tenant?.slug)
    : hasModule(me ?? null, moduleKey, enabled, perms, tenant?.slug)
}

/** Diag (2026-06-05): a SUPER_ADMIN reported /consignment-note bouncing to
 *  home with no console errors. Surface which gate denied so the next
 *  recurrence pinpoints it without DB digging. */
function logModuleDenied(moduleKey: string, me: GateMe, tenant: GateTenant): void {
  const t = tenant ?? { tenantId: null, kind: null, enabledModules: null, slug: null }
  console.warn('[gate] RequireModule denied', {
    moduleKey,
    path: typeof window !== 'undefined' ? window.location.pathname : null,
    meRole: me?.role,
    tenantId: t.tenantId,
    tenantKind: t.kind,
    tenantEnabledModules: t.enabledModules,
    tenantSlug: t.slug,
  })
}

// MoneyGate — the money lock's route half (mig 20260810164741).
//
// Deliberately sited INSIDE RequireModule rather than sprinkled over the
// individual routes: every module route already funnels through here, so one
// gate covers all of them and, more importantly, covers modules added later.
// A per-route opt-in would leak the first time somebody forgot one.
//
// Only `money: 'core'` modules are withheld — the ones that ARE money. The
// 'incidental' ones (receipts, stock, deliveries…) must stay open, because
// they're the capture work the lock exists to protect; their VALUES mask
// instead, via <Money/>.
//
// Unlike the gate above, this renders an explanatory panel rather than
// redirecting home: the operator hasn't done anything wrong and there IS a
// legitimate way in, so bouncing them to the dashboard silently would just
// read as a broken link.
function MoneyGate({ moduleKey, children }: { moduleKey: string; children: ReactNode }) {
  const { locked } = useMoneyLock()
  const mod = MODULES.find(m => m.key === moduleKey)
  if (locked && mod?.money === 'core') return <MoneyLockedPanel moduleLabel={mod.label} />
  return <>{children}</>
}

// RequireTenantKind — strict separation gate between PACKHOUSE and FARM
// tenants. A FARM route accessed while a PACKHOUSE tenant is active
// (or vice-versa) would let an operator capture data against the wrong
// tenant id — e.g. logging a spray record against KF49 packhouse tenant
// instead of the grower's farm tenant. The Dashboard tile filter
// already hides cross-kind tiles, but the URL still routes; this guard
// stops the page from rendering at all and redirects home.
//
// Platform admins are NOT bypassed — when Jason operates "as KF49" or
// "as TJ" via the active_tenant_id JWT claim, the same separation
// applies so he doesn't accidentally write under the wrong tenant
// context during support work. To act in the other context he flips
// the tenant pill explicitly.
function RequireTenantKind(
  { kind, children }: { kind: TenantKind | TenantKind[]; children: ReactNode },
) {
  const allowed = Array.isArray(kind) ? kind : [kind]
  // isPending, not isLoading — same cold-boot-restore reasoning as
  // RequireModule above (finding H4).
  const { data: tenant, isPending } = useActiveTenant()
  if (isPending) return null
  // No tenant claim yet (e.g. brand-new platform-admin with no
  // memberships) → block strict-kind routes rather than allowing writes.
  if (!tenant?.kind) {
    console.warn('[gate] RequireTenantKind denied — no kind', {
      requiredKind: allowed.join('|'),
      path: typeof window !== 'undefined' ? window.location.pathname : null,
      tenantId: tenant?.tenantId ?? null,
    })
    return <Navigate to="/" replace />
  }
  if (!allowed.includes(tenant.kind)) {
    console.warn('[gate] RequireTenantKind denied — wrong kind', {
      requiredKind: allowed.join('|'),
      tenantKind: tenant.kind,
      path: typeof window !== 'undefined' ? window.location.pathname : null,
      tenantId: tenant.tenantId ?? null,
    })
    return <Navigate to="/" replace />
  }
  return <>{children}</>
}

// LockoutGate — runs once after login to detect a session belonging to a
// user who has since been deactivated or locked. Forces sign-out so the
// lock can't be bypassed by holding an old session open.
function LockoutGate({ children }: { children: ReactNode }) {
  const { data: me } = useCurrentUser()
  useEffect(() => {
    if (!me) return
    const isLocked  = me.locked_until && new Date(me.locked_until) > new Date()
    const isInactive = me.is_active === false
    if (isLocked || isInactive) {
      // Sign out and let the unauthed branch render the Login page. Use the
      // hard sign-out so the local token + identity snapshots are cleared even
      // if we're offline — otherwise offlineAuthed would hold a locked user in.
      void signOutEverywhere()
    }
  }, [me])
  return <>{children}</>
}

// TenantStatusGate — wraps the entire authed app and short-circuits to a
// lockout page when the JWT carries tenant_status SUSPENDED / ARCHIVED.
// The auth hook (mig 241) doesn't emit active_tenant_id for those statuses,
// so RLS already blocks every read; this just renders an explanatory page
// instead of a broken empty UI.
//
// While the tenant lookup is loading, render children — the rest of the
// app's queries will also be loading, and a flash of "checking access" is
// worse than a brief render of the dashboard skeleton.
function TenantStatusGate({ children }: { children: ReactNode }) {
  const { data: tenant } = useActiveTenant()
  // Apply the active tenant's product brand to the tab title + OS theme-colour
  // (BigPack for packhouse, BigFarm for farm, …). One mount, authed tree.
  useBrandChrome()
  if (tenant?.status === 'SUSPENDED' || tenant?.status === 'ARCHIVED') {
    return <TenantLockout status={tenant.status} />
  }
  return <>{children}</>
}

// Tiny helper so the route table reads `g('deliveries', <DeliveriesPage/>)`
// instead of repeating the wrapper component everywhere.
const g = (moduleKey: string, element: ReactNode) =>
  <RequireModule moduleKey={moduleKey}>{element}</RequireModule>

// gFarm / gPack — module gate + strict tenant-kind gate. Use these for
// any route that writes data scoped to a specific tenant kind. They
// stop a wrong-tenant-context write at the URL level (e.g. opening
// /farm/orchard while KF49 packhouse is the active tenant would
// otherwise let an operator save against KF49's tenant_id when the
// data belongs to a farm tenant).
// RailShell adds the persistent desktop left rail (nav audit N6, generalised
// from BigExport). It sits in these wrappers rather than in each page because
// every kind-gated route already passes through them — one seam per kind, and
// no page needs to know the rail exists.
const gFarm = (moduleKey: string, element: ReactNode) =>
  <RequireTenantKind kind="FARM"><RequireModule moduleKey={moduleKey}>
    <RailShell appMode="farm">{element}</RailShell>
  </RequireModule></RequireTenantKind>
const gPack = (moduleKey: string, element: ReactNode) =>
  <RequireTenantKind kind="PACKHOUSE"><RequireModule moduleKey={moduleKey}>
    <RailShell>{element}</RailShell>
  </RequireModule></RequireTenantKind>
// gPackAny — gPack for a page several modules open (hasAnyModule). The first
// key stands for the page in the money gate and the denial log.
const gPackAny = (moduleKeys: readonly string[], element: ReactNode) =>
  <RequireTenantKind kind="PACKHOUSE"><RequireModule moduleKey={moduleKeys[0]} anyOf={moduleKeys}>
    <RailShell>{element}</RailShell>
  </RequireModule></RequireTenantKind>
// Labour and teams are reachable by a PAYROLL tenant as well as a packhouse:
// a payroll tenant holds the workers and runs the pay, it just owns no
// operational floor (PAYROLL_TENANT_MODULES). Without this the Labour tile
// renders and then bounces on click — a module that is listed but unreachable.
const gLabour = (moduleKey: string, element: ReactNode) =>
  <RequireTenantKind kind={['PACKHOUSE', 'PAYROLL']}><RequireModule moduleKey={moduleKey}>
    <RailShell>{element}</RailShell>
  </RequireModule></RequireTenantKind>
// /piecework-config (F11 UF-12b): a Config holder as before, OR a labour
// manager — matching the packhouse arms of set_piece_rate's gate (it admits
// can_manage_labour(), and also harvest_rates manage, a harvest key a packhouse
// role does not use). A custom labour-manager role without Config used to be
// bounced to Home off a page whose save the server would accept. The question
// is useCanOpenPieceRates, the same one every pointer to the page asks.
function RequirePieceRates({ children }: { children: ReactNode }) {
  const canOpen = useCanOpenPieceRates()
  if (canOpen === null) return null
  if (!canOpen) {
    console.warn('[gate] RequirePieceRates denied', {
      path: typeof window !== 'undefined' ? window.location.pathname : null,
    })
    return <Navigate to="/" replace />
  }
  // Config and Labour are both money 'incidental' today; keyed on config so a
  // later reclassification of the page's own module still reaches it.
  return <MoneyGate moduleKey="config">{children}</MoneyGate>
}
const gPieceRates = (element: ReactNode) =>
  <RequireTenantKind kind="PACKHOUSE"><RequirePieceRates>
    <RailShell>{element}</RailShell>
  </RequirePieceRates></RequireTenantKind>
// /farm/piecework-config (FM04 UF-14 / UF-14b): a Farm Config holder as before,
// OR anyone holding Farm Labour — a labour manager edits, anyone else (the
// foreman) reads the rates with no edit control. useCanOpenFarmPieceRates is the
// one answer for this guard and every pointer to the page.
function RequireFarmPieceRates({ children }: { children: ReactNode }) {
  const canOpen = useCanOpenFarmPieceRates()
  if (canOpen === null) return null
  if (!canOpen) {
    console.warn('[gate] RequireFarmPieceRates denied', {
      path: typeof window !== 'undefined' ? window.location.pathname : null,
    })
    return <Navigate to="/" replace />
  }
  return <MoneyGate moduleKey="farm_config">{children}</MoneyGate>
}
const gFarmPieceRates = (element: ReactNode) =>
  <RequireTenantKind kind="FARM"><RequireFarmPieceRates>
    <RailShell appMode="farm">{element}</RailShell>
  </RequireFarmPieceRates></RequireTenantKind>
const gAgent = (moduleKey: string, element: ReactNode) =>
  <RequireTenantKind kind="AGENT"><RequireModule moduleKey={moduleKey}>{element}</RequireModule></RequireTenantKind>
const gSupplier = (moduleKey: string, element: ReactNode) =>
  <RequireTenantKind kind="SUPPLIER"><RequireModule moduleKey={moduleKey}>{element}</RequireModule></RequireTenantKind>
const gExporter = (moduleKey: string, element: ReactNode) =>
  <RequireTenantKind kind="EXPORTER"><RequireModule moduleKey={moduleKey}>
    <RailShell>{element}</RailShell>
  </RequireModule></RequireTenantKind>
// TRANSPORTER tenants (BigLogistics). Kind gate + module gate, same shape as
// gSupplier — the carrier's own surfaces only; trip data crosses in P1 through
// SECDEF readers keyed on the linked_transporter relationship, never RLS here.
const gTrans = (moduleKey: string, element: ReactNode) =>
  <RequireTenantKind kind="TRANSPORTER"><RequireModule moduleKey={moduleKey}>
    <RailShell>{element}</RailShell>
  </RequireModule></RequireTenantKind>
// PLATFORM tenant (BigAgri's own billing tenant). Kind gate + module gate; the
// billing RPCs behind it re-check can_run_saas_billing() on the server.
const gPlatform = (moduleKey: string, element: ReactNode) =>
  <RequireTenantKind kind="PLATFORM"><RequireModule moduleKey={moduleKey}>{element}</RequireModule></RequireTenantKind>
// HARVEST tenants (BigHarvest — harvest labour contractors). Kind gate +
// module gate, same shape as gTrans. Everything a contractor captures is
// plain tenant-scoped data; no edge crosses in P0 (docs/specs/big-harvest.md).
const gHarvest = (moduleKey: string, element: ReactNode) =>
  <RequireTenantKind kind="HARVEST"><RequireModule moduleKey={moduleKey}>{element}</RequireModule></RequireTenantKind>
// SECURITY tenants (BigSecurity — security companies). Kind gate + module
// gate, same shape as gHarvest. Routes live under /bigsecurity/* because
// /security/* is the Phase 1 packhouse register (docs/specs/bigsecurity-tenant.md).
const gSecurity = (moduleKey: string, element: ReactNode) =>
  <RequireTenantKind kind="SECURITY"><RequireModule moduleKey={moduleKey}>{element}</RequireModule></RequireTenantKind>
// /bigsecurity/incidents/:incidentId opens the register with that incident up (?incident=, UF-21).
function SecurityIncidentLink() {
  const { incidentId = '' } = useParams<{ incidentId: string }>()
  return <Navigate to={`/bigsecurity/incidents?incident=${encodeURIComponent(incidentId)}`} replace />
}
// BUYER tenants: the legacy dashboard stays kind-gated only (it reads via
// get_my_buyer_portal, scoped to buyer_exporter_links); BigTrade 3b buyer
// modules are module-gated like every other kind.
const gBuyer = (element: ReactNode) =>
  <RequireTenantKind kind="BUYER">{element}</RequireTenantKind>
const gBuyerM = (moduleKey: string, element: ReactNode) =>
  <RequireTenantKind kind="BUYER"><RequireModule moduleKey={moduleKey}>{element}</RequireModule></RequireTenantKind>

// RequireFranchiseAdmin — gate for /franchise routes. Three checks:
//   1. Active tenant is AGENT-kind (already covered by gAgent, but we
//      re-assert here so the gate is self-contained).
//   2. That tenant carries a franchise_id (independent agents have NULL
//      and can't see /franchise admin surfaces).
//   3. Caller's role is FRANCHISE_ADMIN. Mig 330 introduced this role;
//      mig 353 fixed the cross-tenant gate helper.
//
// All three must hold or we Navigate home — same fail-closed posture as
// RequireTenantKind. A franchise admin who flips their active tenant to
// a non-AGENT context (support work) loses access to /franchise until
// they flip back; that's intentional.
function RequireFranchiseAdmin({ children }: { children: ReactNode }) {
  // isPending, not isLoading — same cold-boot-restore reasoning as
  // RequireModule above (finding H4).
  const { data: tenant, isPending: tenantPending } = useActiveTenant()
  const { data: me,     isPending: mePending }     = useCurrentUser()
  if (tenantPending || mePending) return null
  if (tenant?.kind !== 'AGENT')             return <Navigate to="/" replace />
  if (!tenant?.franchiseId)                 return <Navigate to="/" replace />
  if (me?.role !== 'FRANCHISE_ADMIN')       return <Navigate to="/" replace />
  return <>{children}</>
}

const gFranchise = (moduleKey: string, element: ReactNode) =>
  <RequireFranchiseAdmin><RequireModule moduleKey={moduleKey}>{element}</RequireModule></RequireFranchiseAdmin>

// Router root. The render-route branch above the auth split adds one more
// arm to what is already the app's routing table; splitting it would scatter
// the auth-state decision it exists to express.
// fallow-ignore-next-line complexity
export default function App() {
  const [session, setSession] = useState<Session | null | undefined>(undefined)

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session))
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => setSession(s))
    return () => subscription.unsubscribe()
  }, [])

  // Multi-tab tenant-switch listener (council T1.6). Tab A's switcher
  // posts a message after a successful switch; every other tab reloads
  // so they boot with the fresh JWT instead of writing rows against the
  // stale active_tenant_id. Without this, Tab B keeps its 5-min cached
  // tenantId AND its 1-hour stale JWT - writes silently cross-tenant.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return
    const bc = new BroadcastChannel('kf49.tenant-switch')
    bc.onmessage = (ev: MessageEvent) => {
      // Mirror the switching tab so background tabs aren't the jarring exception:
      // (1) seed the boot-overlay flag with the carried tenant name, and (2) drop
      // the previous tenant's cached API responses (the SW caches /rest/v1 by URL
      // not tenant — Tab B would otherwise serve stale cross-tenant rows). Then
      // hard reload to rehydrate supabase-js from the freshly-written session.
      try { sessionStorage.setItem('kf49.switching', (ev?.data?.name as string) || '1') } catch { /* */ }
      void Promise.race([
        (caches?.delete?.('supabase-api-cache') ?? Promise.resolve()).catch(() => {}),
        new Promise<void>(r => setTimeout(r, 1500)),
      ]).finally(() => window.location.reload())
    }
    return () => bc.close()
  }, [])

  // Tenant-switch boot overlay. Dashboard.switchTo wrote a sessionStorage flag
  // before reloading; render a matching skeleton over the boot so the switch
  // reads as one continuous loading state (click → reload → new tenant) with no
  // flash of stale data. Read + clear the flag once on mount; auto-dismiss with
  // a short fade so a stuck flag can never trap the user behind the skeleton.
  const [switchBoot, setSwitchBoot] = useState<string | null>(() => {
    // Read but DON'T clear yet — a stale-chunk auto-reload (lazyWithReload)
    // during the switch boot must still find the flag so the overlay re-shows.
    // Cleared on dismiss below.
    try { return sessionStorage.getItem('kf49.switching') } catch { return null }
  })
  const [switchBootClosing, setSwitchBootClosing] = useState(false)
  useEffect(() => {
    if (switchBoot === null) return
    let unmount: ReturnType<typeof setTimeout> | undefined
    const dismiss = () => {
      setSwitchBootClosing(true)
      unmount = setTimeout(() => {
        setSwitchBoot(null)
        try { sessionStorage.removeItem('kf49.switching') } catch { /* */ }
      }, 300) // matches the overlay's 300ms fade
    }
    // Dismiss on READINESS, not a blind timer: once the session has resolved
    // (past the bare boot spinner), hold one paint so the dashboard commits,
    // then fade. A hard cap guarantees a stuck/slow boot can never trap the user
    // behind the full-screen overlay.
    const ready = session !== undefined
      ? setTimeout(() => requestAnimationFrame(dismiss), 350)
      : undefined
    const cap = setTimeout(dismiss, 3500)
    return () => { if (ready) clearTimeout(ready); clearTimeout(cap); if (unmount) clearTimeout(unmount) }
  }, [switchBoot, session])
  const bootOverlay = switchBoot !== null
    ? <TenantSwitchOverlay name={switchBoot === '1' ? null : switchBoot} closing={switchBootClosing} />
    : null

  // Still loading session
  if (session === undefined) {
    return (
      <>
        {bootOverlay}
        <div className="h-full flex flex-col items-center justify-center gap-3">
          <BrandLoader />
          <LoadingWords />
        </div>
      </>
    )
  }

  // Stored-session gate. The access token expires ~1h after the last refresh;
  // with no — or, crucially, MARGINAL — connectivity supabase-js can't refresh,
  // so getSession() returns null. But the user IS logged in: their session is
  // still in localStorage. Keep them in the authed shell whenever a stored
  // session exists, so a field scout isn't bounced to a Login they can't
  // complete.
  //
  // This must NOT gate on navigator.onLine (the old isOffline() check). That
  // flag is only reliably false in airplane-mode; on a weak/EDGE link or a
  // captive portal — the COMMON farm-field case — it reports `true` while the
  // refresh still fails, so the offline-recovery never engaged and scouts were
  // kicked to Login. Dropping the onLine condition fixes that.
  //
  // Still fail-closed on a genuine sign-out/revoke: supabase-js CLEARS the
  // stored token (and emits SIGNED_OUT) when an ONLINE refresh is rejected as
  // revoked, and LockoutGate's signOutEverywhere() clears it for a deactivated
  // user even offline — so hasStoredSession() goes false and the next render
  // shows Login. A network-failed refresh, by contrast, leaves the token in
  // place (that's what readStoredSession/decodeStoredClaims rely on). See
  // src/lib/offlineSession.ts.
  const offlineAuthed = !session && hasStoredSession()

  // ---------------------------------------------------------------------
  // PDF render routes — ABOVE the auth split, deliberately.
  //
  // The renderer arrives with no session, redeems its render token, and THEN
  // signs in as the sending operator. So the page starts unauthenticated and
  // becomes authenticated mid-life. Registered only in the signed-out branch,
  // it vanished the instant verifyOtp resolved and the app redirected the
  // renderer to the dashboard — which printed as a dashboard screenshot, not
  // a statement. Registered only in the authed branch, it would never mount
  // at all. It has to be in both, so it lives before the split.
  //
  // Never linked to from the UI; the only caller is the renderer. The
  // capability is the cookie'd render token: single-use, 60s, bound to this
  // exact URL (mig 20260816100000).
  // ---------------------------------------------------------------------
  if (window.location.pathname.startsWith('/render/')) {
    return (
      <Routes>
        <Route path="/render/grower-statement" element={<Suspense fallback={PageFallback}><GrowerStatementRenderPage /></Suspense>} />
        {/* Phase 1.5: a report page mounted unchanged inside <RenderRoute/>,
            which establishes the operator's session and nothing else. The QC
            page already carries a full print stylesheet and print:hidden
            chrome, so what the renderer prints IS the printed report. */}
        <Route path="/render/qc-report" element={<Suspense fallback={PageFallback}><RenderRoute><QCReportPage /></RenderRoute></Suspense>} />
        <Route path="/render/invoice" element={<Suspense fallback={PageFallback}><RenderRoute><InvoicePage /></RenderRoute></Suspense>} />
        {/* Payroll documents for the business's own HR office — the run's
            payslips and the EMP201 reconciliation sheet — mounted unchanged;
            both pages read their subject from the query string here. */}
        <Route path="/render/payslips" element={<Suspense fallback={PageFallback}><RenderRoute><PayslipsBulkPage /></RenderRoute></Suspense>} />
        <Route path="/render/emp201-recon" element={<Suspense fallback={PageFallback}><RenderRoute><Emp201ReconPage /></RenderRoute></Suspense>} />
        {/* A purchase order to its supplier (docs/specs/purchase-orders.md P1):
            the page reads ?po= here and :poId in-app. */}
        <Route path="/render/purchase-order" element={<Suspense fallback={PageFallback}><RenderRoute><PurchaseOrderPrintPage /></RenderRoute></Suspense>} />
        {/* A quote to its client (docs/specs/quotes-and-client-prices.md Q2):
            the page reads ?quote= here and :quoteId in-app. */}
        <Route path="/render/quote" element={<Suspense fallback={PageFallback}><RenderRoute><QuotePrintPage /></RenderRoute></Suspense>} />
        {/* A fruit delivery's note, with the farmer's paperwork photos, to its
            grower: the page reads ?delivery= here and :deliveryId in-app. */}
        <Route path="/render/delivery-note" element={<Suspense fallback={PageFallback}><RenderRoute><DeliveryNotePrintPage /></RenderRoute></Suspense>} />
        {/* Our SaaS subscription invoice to a business (mig 20261028200000),
            rendered AS the platform admin who pressed Send; the page reads
            ?invoice= here and :invoiceId in-app. */}
        <Route path="/render/saas-invoice" element={<Suspense fallback={PageFallback}><RenderRoute><SaasInvoicePrintPage /></RenderRoute></Suspense>} />
        {/* Our SaaS statement of account to a business (mig 20261042900000),
            rendered AS the billing admin who pressed Send; the page reads
            ?tenant=&from=&to= here and :tenantId in-app. */}
        <Route path="/render/saas-statement" element={<Suspense fallback={PageFallback}><RenderRoute><SaasStatementPrintPage /></RenderRoute></Suspense>} />
        <Route path="*" element={null} />
      </Routes>
    )
  }

  if (!session && !offlineAuthed) {
    return (
      <Routes>
        {/* Public grower portals — no auth required, RLS gates by token */}
        <Route path="/g/:runId" element={<GrowerPortalPage />} />
        {/* Public worker self-serve payslip — anon-granted RPC gates by share_token (mig 398) */}
        <Route path="/payslip-share/:token" element={<PayslipSharePage />} />
        {/* POPIA worker portal — anon by design (most field workers don't
            have logins). Token-gated, mig 664. */}
        <Route path="/worker-portal/:token"  element={<WorkerPortalPage />} />
        {/* A grower signs a packing contract — anon by design (growers are not
            platform users). Token-gated, mig 20261228000000. */}
        <Route path="/sign/:token"           element={<PackingContractSignPage />} />
        {/* Buyer / importer shipment tracker — anon by design (buyers aren't
            platform users). Token-gated, mig 1001 / 1004. */}
        <Route path="/track/:token"          element={<BuyerTrackPage />} />
        {/* Grower home AND buyer tracker — one path, split on ?t= (PortalRoute). */}
        <Route path="/portal/:portalId"      element={<PortalRoute />} />
        {/* Face-clocking kiosk — pre-auth by design. Device-token-paired,
            scans clock events into clock_scans via the face-clock-in edge
            function. Mounts on tablets that never log a user in. */}
        <Route path="/clock-face" element={<Suspense fallback={PageFallback}><ClockFacePage /></Suspense>} />
        {/* Device sign-in — pre-auth for the same reason /clock-face is: the
            device is paired, and NOBODY is signed in until a badge and a face
            say who is standing there. Unlike the kiosk this one MINTS a
            session, so the face is judged server-side (spec §4.4). */}
        <Route path="/device-signin" element={<Suspense fallback={PageFallback}><DeviceLockScreenPage /></Suspense>} />
        {/* ⚠️ PRE-AUTH TOO, and that looks alarming until you see what it can
            do. Offline there is NO Supabase session to gate on — the whole
            point of D11 is that a new person in a dead spot cannot be minted
            one — so a session guard here would make the screen unreachable in
            exactly the case it exists for. It is gated on a LOCAL session
            instead, and a local session grants nothing: the screen adds to a
            signed queue and reads nothing at all. Every entry is re-verified
            server-side on drain (§5 rule 2) before it counts. */}
        <Route path="/device-capture" element={<Suspense fallback={PageFallback}><DeviceCaptureScreenPage /></Suspense>} />
        <Route path="/login" element={<LoginPage />} />
        {/* Sign in with Google / Microsoft returns here with a single-use code
            (src/lib/authDirect.ts). Signed-out tree only: the buttons live on
            /login, and a signed-in tab has nothing to exchange. */}
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        {/* Self-serve onboarding — public, approval-gated. Submits a request
            (request-tenant-signup); a platform admin approves before any tenant
            is created. Phase 0A. GATED OFF until SMTP + go-ahead (signupFlag +
            the edge fn's PUBLIC_SIGNUP_ENABLED env); /signup → /login while off. */}
        <Route path="/signup" element={PUBLIC_SIGNUP_ENABLED ? <SignupTenantPage /> : <Navigate to="/login" replace />} />
        {/* Invite/recovery landing — Supabase processes the hash, then this
            page lets the new user set their password. Reachable both with
            and without an active session because the hash auto-creates one
            asynchronously. */}
        <Route path="/accept-invite" element={<AcceptInvitePage />} />
        {/* POPIA — the privacy policy must be reachable without an
            account, because invitees + prospective customers need to
            see it before they can decide to use the platform. */}
        <Route path="/privacy" element={<Suspense fallback={PageFallback}><PrivacyPolicyPage /></Suspense>} />
        {/* ECTA s43(1) — a prospective tenant must be able to read the terms
            BEFORE concluding the transaction, so /terms is pre-auth too. */}
        <Route path="/terms" element={<Suspense fallback={PageFallback}><TermsOfServicePage /></Suspense>} />
        {/* An AI assistant's OAuth sign-in lands here (Supabase Auth's OAuth
            server → Site URL + /oauth/consent?authorization_id=…). Signed out,
            the sign-in form renders AT this URL instead of bouncing to /login,
            so the authorization_id survives and the consent page appears the
            moment the session lands (MCP pilot, docs/runbooks/BIGAGRI-MCP-PILOT.md). */}
        <Route path="/oauth/consent" element={<LoginPage />} />
        {/* Third-party licence notices — public like /privacy: the bundle they
            cover reaches a browser before anyone signs in. */}
        <Route path="/licences" element={<Suspense fallback={PageFallback}><OpenSourceLicencesPage /></Suspense>} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }

  return (
    <>
    {bootOverlay}
    {/* ⚠️ Shared-device idle sign-outs — OUTSIDE every gate below. Each gate
        renders its own screen INSTEAD OF the app when it fires; mounted among
        the app they unmounted with it, and a tablet left on the consent prompt
        stayed signed in as the last worker indefinitely. Both are no-ops on a
        machine that is neither a paired app device nor in kiosk mode, so an
        office computer is never signed out mid-task. */}
    <SharedDeviceSignOuts />
    <LockoutGate>
    <ConsentGate>
    <TenantStatusGate>
    {/* Every app-wide status banner lives in the ONE fixed TopBanners strip.
        Fixed, not in-flow: an in-flow banner steals its height from the
        h-full chain and pushes every bounded scroller under the bottom nav
        (#1963 S2-1). The strip measures itself into --top-inset, which
        <body> reads as padding-top, so #root shrinks by the banner instead. */}
    <TopBanners>
      <SupportViewBanner />
      <PlatformModeBanner />
      {/* Reads-paused strip — onlineManager-driven (the flag that actually
          pauses queries), distinct from the write-queue badge (idle-wake
          audit P1.7). */}
      <QueriesPausedBanner />
    </TopBanners>
    <AppearanceOnNavigate />
    <ScrollToTop />
    <UiTelemetryRouteListener />
    <Suspense fallback={PageFallback}>
    <Routes>
      {/* The rail rides on Home too (kinds without one render nothing), so it
          never vanishes between a page and the hub. */}
      <Route path="/"           element={<RailShell><DashboardPage /></RailShell>} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      {/* POPIA — privacy policy + self-service Subject Access Request.
          Both accessible without a module flag; SAR is per-user via
          users.id = auth.uid() (no tenant gate). */}
      <Route path="/privacy"          element={<PrivacyPolicyPage />} />
      <Route path="/terms"            element={<TermsOfServicePage />} />
      <Route path="/privacy/my-data"  element={<SubjectAccessRequestPage />} />
      {/* "Connect your AI assistant" — approve/deny an assistant's OAuth sign-in
          and disconnect connected ones. No module gate: it is the person's own
          account, like /privacy/my-data; the MCP server re-checks every rule. */}
      <Route path="/oauth/consent"    element={<OAuthConsentPage />} />
      <Route path="/licences"         element={<OpenSourceLicencesPage />} />
      <Route path="/deliveries"         element={gPack('deliveries', <DeliveriesStage />)} />
      <Route path="/deliveries/weekly"  element={gPack('deliveries', <DeliveriesWeeklyPage />)} />
      <Route path="/deliveries/report"  element={gPack('deliveries', <DeliveriesReportStage />)} />
      <Route path="/deliveries/crate-weights" element={gPack('deliveries', <CrateWeightsPage />)} />
      <Route path="/deliveries/cultivar-report" element={gPack('deliveries', <CultivarReportPage />)} />
      {/* Externally-packed pallet intake (mig 20260807090000). Both pages
          shipped in #1281 with NO route: the Deliveries header button sent
          /deliveries/receive, which fell through to :deliveryId below and
          rendered the delivery detail for a delivery id of "receive", while
          /deliveries/receive/:receiptId hit the catch-all. Keep them listed
          above :deliveryId — the router ranks static over dynamic either way,
          but the collision is the whole reason these lines exist. */}
      <Route path="/deliveries/receive" element={gPack('deliveries', <ReceivePalletsPage />)} />
      <Route path="/deliveries/receive/new" element={gPack('deliveries', <ReceiveArrivalPage />)} />
      <Route path="/deliveries/receive/:receiptId" element={gPack('deliveries', <PalletReceiptPage />)} />
      <Route path="/deliveries/receive/:receiptId/report" element={gPack('deliveries', <PalletReceiptReportPage />)} />
      <Route path="/deliveries/receive/:receiptId/packout" element={gPack('deliveries', <ReceiptPackoutReportPage />)} />
      <Route path="/deliveries/:deliveryId" element={gPack('deliveries', <DeliveryDetailPage />)} />
      <Route path="/deliveries/:deliveryId/note" element={gPack('deliveries', <DeliveryNotePrintPage />)} />
      {/* The intake sticker for a delivery's fruit (spec #3956 §3). */}
      <Route path="/delivery-label/:deliveryId" element={gPack('deliveries', <DeliveryLabelPage />)} />
      <Route path="/tipping"    element={gPack('tipping', <TippingPage />)} />
      <Route path="/tipping/run/:runId" element={gPack('tipping', <TippingPage />)} />
      <Route path="/runs"       element={gPack('runs', <RunsPage />)} />
      {/* The run detail has a URL (plan §2D 3): the register opens it in
          place and deep links land on it; the print-friendly trace page
          that used to sit on this path lives one segment down. */}
      <Route path="/runs/:runId" element={gPack('runs', <RunsPage />)} />
      <Route path="/runs/:runId/print" element={gPack('runs', <RunDetailPage />)} />
      <Route path="/pallets"    element={gPack('pallets', <PalletsPage />)} />
      <Route path="/pallets/uncoded"   element={gPack('pallets', <UncodedPalletsPage />)} />
      <Route path="/pallets/:palletId" element={gPack('pallets', <PalletDetailPage />)} />
      <Route path="/growers"     element={gPack('growers', <GrowersPage />)} />
      <Route path="/growers/report" element={gPack('growers', <GrowersOverallReportPage />)} />
      <Route path="/growers/census" element={gPack('growers', <OrchardCensusPage />)} />
      <Route path="/growers/:growerId" element={gPack('growers', <GrowerDetailPage />)} />
      <Route path="/growers/:growerId/report" element={gPack('growers', <GrowerReportPage />)} />
      {/* Packing contracts (mig 20261228000000) — under the growers module; the
          RPCs and RLS admit ADMIN / MANAGER only, and the pages say so. */}
      <Route path="/packing-contracts" element={gPack('growers', <PackingContractsPage />)} />
      <Route path="/packing-contracts/templates/:templateId" element={gPack('growers', <PackingContractTemplatePage />)} />
      <Route path="/packing-contracts/:contractId" element={gPack('growers', <PackingContractPage />)} />
      <Route path="/qc"         element={gPack('qc', <QCStage />)} />
      {/* The picked run lives in the URL so a QC check is linkable — "look at
          the checks on run 1183" was previously unsendable, because the live
          surface kept the run in localStorage while only the REPORT had a
          route. Same page; QCPage reads :runId. */}
      <Route path="/qc/run/:runId" element={gPack('qc', <QCStage />)} />
      <Route path="/qc/run/:runId/report" element={gPack('qc', <QCReportPage />)} />
      {/* QC home → Print report: many runs in one document (?variety=&approved=1…). */}
      <Route path="/qc/report" element={gPack('qc', <QcMultiRunReportPage />)} />
      <Route path="/inspection" element={gPack('inspection', <InspectionPage />)} />
      <Route path="/dispatch"   element={gPack('dispatch', <DispatchPage />)} />
      <Route path="/dispatch/planner" element={gPack('dispatch', <TruckPlannerPage />)} />
      <Route path="/trade-listings" element={gPack('trade_listings', <TradeListingsPage />)} />
      <Route path="/missions/:missionId" element={gPack('dispatch', <MissionDetailPage />)} />
      <Route path="/pack-ref-codes" element={gPack('orders', <PackRefCodesPage />)} />
      <Route path="/pack-instructions" element={gPack('orders', <PackInstructionsPage />)} />
      <Route path="/pack-instructions/:id" element={gPack('orders', <PackInstructionDetailPage />)} />
      <Route path="/pack-instruction-print/:id" element={gPack('orders', <PackInstructionPrintPage />)} />
      <Route path="/orders" element={gPack('orders', <OrdersPage />)} />
      <Route path="/orders/:id" element={gPack('orders', <OrderDetailPage />)} />
      <Route path="/orders-production-sheet" element={gPack('orders', <ProductionSheetPage />)} />
      <Route path="/order-print/:id" element={gPack('orders', <OrderPrintPage />)} />
      <Route path="/products" element={gPack('orders', <ProductsPage />)} />
      <Route path="/loading-bay" element={gPack('loading_bay', <LoadingBayPage />)} />
      <Route path="/coldroom"   element={gPack('coldroom', <ColdroomPage />)} />
      <Route path="/coldroom/report" element={gPack('coldroom', <ColdroomReportPage />)} />
      <Route path="/coldroom/temperatures/log" element={gPack('coldroom', <ColdroomTemperatureLogPage />)} />
      <Route path="/coldroom/cold-treatment/log" element={gPack('coldroom', <ColdTreatmentLogPage />)} />
      <Route path="/rebin"      element={gPack('rebin', <RebinPage />)} />
      <Route path="/bin-label/:binId" element={gPackAny(BIN_PAGE_MODULES, <BinLabelPage />)} />
      <Route path="/bin-labels" element={gPackAny(BIN_PAGE_MODULES, <BinLabelsPage />)} />
      <Route path="/bins/:binId" element={gPackAny(BIN_PAGE_MODULES, <BinDetailPage />)} />
      <Route path="/trace/:palletId" element={gPack('pallets', <TraceReportPage />)} />
      <Route path="/label/pallet/:palletId" element={gPack('pallets', <LabelPrintPage kind="pallet" />)} />
      <Route path="/label/carton/:palletId" element={gPack('pallets', <LabelPrintPage kind="carton" />)} />
      <Route path="/label/pallets/batch"    element={gPack('pallets', <LabelPrintPage kind="pallet" />)} />
      <Route path="/printing-setup" element={gPack('pallets', <PrintingSetupPage />)} />
      <Route path="/label-designer" element={gPack('pallets', <LabelDesignerPage />)} />
      <Route path="/monitor" element={gPack('monitor', <FloorMonitorPage />)} />
      {/* Ask BigAgri floor mode — cross-module (no RequireModule); the add-on
          gate self-renders inside and the server gate is authoritative.
          FARM tenants too (Jason, 2 Oct 2026): the page itself shows a farm
          member below manager tier why it is closed — the server refuses them. */}
      <Route path="/ask" element={<RequireTenantKind kind={['PACKHOUSE', 'FARM']}><AskFloorPage /></RequireTenantKind>} />
      <Route path="/recommendations" element={gPack('recommendations', <RecommendationsPage />)} />
      <Route path="/maintenance" element={gPack('maintenance', <MaintenancePage />)} />
      <Route path="/maintenance/wo/:id" element={gPack('maintenance', <WorkOrderDetailPage />)} />
      <Route path="/maintenance/asset/:id" element={gPack('maintenance', <AssetDetailPage />)} />
      <Route path="/maintenance/report" element={gPack('maintenance', <MaintenanceReportPage />)} />
      <Route path="/calendar" element={gPack('calendar', <CalendarPage />)} />
      <Route path="/pack-schedule" element={gPack('pack_schedule', <PackSchedulePage />)} />
      <Route path="/pack-programme" element={gPack('pack_programme', <PackProgrammePage />)} />
      <Route path="/pack-programme/print" element={gPack('pack_programme', <PackProgrammePrintPage />)} />
      <Route path="/pack-sheet" element={gPack('pack_programme', <PackSheetReadPage />)} />
      <Route path="/line-layout"   element={gPack('line_layout', <LineLayoutPage />)} />
      {/* Old bookmark — Downtime became Maintenance (mig 739). */}
      <Route path="/downtime" element={<Navigate to="/maintenance?tab=downtime" replace />} />
      <Route path="/yard" element={gPack('yard', <YardPage />)} />
      <Route path="/bin-inventory" element={gPack('bin_inventory', <BinInventoryPage />)} />
      <Route path="/precool" element={gPack('precool', <PrecoolStage />)} />
      <Route path="/precool/report" element={gPack('precool', <PrecoolReportPage />)} />
      <Route path="/batch-label/:batchId" element={gPack('precool', <BatchLabelPage />)} />
      <Route path="/bin-inventory/history/report" element={gPack('bin_inventory', <BinHistoryReportPage />)} />
      <Route path="/bin-inventory/cages/report"   element={gPack('bin_inventory', <CagesReportPage />)} />
      <Route path="/rejects"    element={gPack('rejects', <RejectsPage />)} />
      <Route path="/receipts"   element={gPack('receipts', <ReceiptsPage />)} />
      <Route path="/config"     element={gPack('config', <ConfigPage />)} />
      <Route path="/activity"   element={gPack('activity', <ActivityPage />)} />
      <Route path="/audit"      element={gPack('activity', <TenantAuditPage />)} />
      {/* The platform console and its tools (/platform, /support, /tenant-signups,
          /platform-audit, …) live in the admin app (admin.bigagri.app, src/admin).
          Their old paths send a platform admin there and anyone else home. */}
      {Object.keys(MOVED_TO_ADMIN_APP).map(path => (
        <Route key={path} path={path} element={<MovedToAdminAppPage />} />
      ))}
      {/* Support-view start — the admin app (admin.bigagri.app) links here so the
          support view starts in THIS app's session (a claim minted in the admin
          app's token never reaches this origin). Bare route; the page gates on
          RequirePlatformAdmin and starts nothing until its button is pressed. */}
      <Route path="/support-view" element={<SupportViewStartPage />} />
      {/* Industry benchmarks — the Data product (#6). Admin/manager; gated on
          the tenant's data-sharing opt-in (reciprocity). Shared farm+packhouse. */}
      <Route path="/benchmarks" element={<BenchmarksPage />} />
      <Route path="/stock"      element={gPack('stock', <StockPage />)} />
      <Route path="/security"   element={gPack('security', <SecurityIncidentsPage />)} />
      <Route path="/security/tags" element={gPack('security', <SecurityCheckpointTagsPage />)} />
      <Route path="/security/:incidentId/print" element={gPack('security', <SecurityIncidentPrintPage />)} />
      <Route path="/purchasing" element={gPack('purchasing', <PurchaseOrdersPage />)} />
      <Route path="/purchasing/checks" element={gPack('purchasing', <PurchaseChecksPage />)} />
      <Route path="/purchasing/:poId" element={gPack('purchasing', <PurchaseOrderDetailPage />)} />
      <Route path="/purchasing/:poId/print" element={gPack('purchasing', <PurchaseOrderPrintPage />)} />
      <Route path="/stock/report" element={gPack('stock', <StockReportPage />)} />
      <Route path="/stock/categories" element={gPack('stock', <StockCategoriesPage />)} />
      <Route path="/stock/counts" element={gPack('stock', <StockCountsPage />)} />
      <Route path="/stock/counts/sheet" element={gPack('stock', <StockCountSheetPage />)} />
      <Route path="/stock/recall" element={gPack('stock', <StockRecallPage />)} />
      <Route path="/stock/locations" element={gPack('stock', <StockLocationsPage />)} />
      <Route path="/stock/owners" element={gPack('stock', <StockOwnersPage backTo="/stock" />)} />
      <Route path="/stock/scan" element={gPack('stock', <StockScanPage />)} />
      <Route path="/stock/labels" element={gPack('stock', <StockLabelsPage />)} />
      <Route path="/suppliers"  element={gPack('suppliers', <SuppliersPage />)} />
      <Route path="/reconciliation" element={gPack('reconciliation', <ReconciliationPage />)} />
      <Route path="/rate-card/:productId" element={gPack('financial', <RateCardPage />)} />
      <Route path="/commodities" element={gPack('commodities', <CommoditiesAdminPage />)} />
      <Route path="/packers"    element={gPack('packers', <PackersPage />)} />
      <Route path="/users"      element={g('users', <UsersPage />)} />
      <Route path="/addons"     element={<AddonsPage />} />
      {/* Billing — module-gate-free on purpose: the page that fixes a payment
          problem must never be hideable by module/subscription config. */}
      <Route path="/billing"    element={<BillingPage />} />
      {/* One SaaS invoice, printable (mig 20261028200000). Gate-free like
          /billing; the read itself admits only the business's admins and
          platform admins. */}
      <Route path="/billing/invoices/:invoiceId" element={<SaasInvoicePrintPage />} />
      <Route path="/financial"  element={gPack('financial', <FinancialPage />)} />
      <Route path="/market-prices" element={gPack('market_prices', <MarketPricesPage />)} />
      <Route path="/financial/upload" element={gPack('financial', <UploadCostingsPage />)} />
      <Route path="/quotes/:quoteId" element={gPack('financial', <QuoteBuilderPage />)} />
      <Route path="/quotes/:quoteId/print" element={gPack('financial', <QuotePrintPage />)} />
      <Route path="/compliance" element={gPack('compliance', <CompliancePage />)} />
      <Route path="/compliance/audit-pack" element={gPack('compliance', <ComplianceAuditPackPage />)} />
      {/* My Checklists — per-user landing aggregating compliance + QC
          checklists assigned to the caller (mig 584). Open to any
          authenticated user regardless of tenant kind: a FARM-tenant
          user with farm-side compliance checklists sees the same page. */}
      <Route path="/my-checklists" element={<MyChecklistsPage />} />
      <Route path="/safety/report" element={gPack('safety_report', <PackhouseReportIncidentPage />)} />
      <Route path="/estimates"  element={gPack('estimates', <EstimatesPage />)} />
      <Route path="/estimates/report" element={gPack('estimates', <EstimatesReportPage />)} />
      <Route path="/reject-bins"           element={gPack('reject_bins', <RejectBinsPage />)} />
      <Route path="/reject-bins/:binId"    element={gPack('reject_bins', <RejectBinDetailPage />)} />
      {/* Mig 472–476 — multi-grower / multi-stop reject consignments
          living on the unified loading_missions table. Standalone surface
          mirroring the pallet Dispatch.tsx flow. */}
      <Route path="/reject-dispatch"             element={gPack('reject_dispatch', <RejectDispatchPage />)} />
      <Route path="/reject-dispatch/:missionId"  element={gPack('reject_dispatch', <RejectDispatchPage />)} />
      <Route path="/reject-dispatch/:missionId/print" element={gPack('reject_dispatch', <RejectDispatchPrintPage />)} />
      {/* Mig 351 batch-release (reject_dispatches) reporting + per-dispatch
          receipt, reached from /reject-bins → Dispatches tab. */}
      <Route path="/reject-dispatches/report"              element={gPack('reject_bins', <RejectDispatchesReportPage />)} />
      <Route path="/reject-dispatches/:dispatchId/receipt" element={gPack('reject_bins', <RejectDispatchReceiptPage />)} />
      {/* Redirect bookmarks for the old standalone Reject Buyers page
          to the new tab on /reject-bins. */}
      <Route path="/reject-buyers"         element={<Navigate to="/reject-bins?tab=buyers" replace />} />
      <Route path="/labour"                element={gLabour('labour', <LabourPage category="PACKHOUSE" />)} />
      {/* Packer teams + team piece-work (packout tallies are team-stamped at
          import, mig 20260710160000). Rates page mirrors /farm/piecework-config;
          it admits Config holders AND labour managers (F11 UF-12b, gPieceRates). */}
      <Route path="/teams"                 element={gLabour('teams', <PackhouseTeamsPage />)} />
      {/* Team-pallet roll call (mig 20261080000000): same module gate as Teams;
          the page itself says when the business has team pallets off. */}
      <Route path="/teams/roll-call"       element={gLabour('teams', <TeamRollCallPage />)} />
      <Route path="/team-performance"      element={gPack('team_performance', <PackhouseTeamPerformancePage />)} />
      <Route path="/organogram"            element={gLabour('organogram', <OrganogramPage />)} />
      <Route path="/piecework-config"      element={gPieceRates(<PackhousePieceworkConfigPage />)} />
      {/* The capture station (spec packhouse-piecework-capture.md). gPack, not
          gLabour: it is a packhouse FLOOR surface — a payroll tenant holds the
          workers but owns no line to capture on. Its own module key so a line
          supervisor can be given capture without payroll. */}
      <Route path="/packer-capture"        element={gPack('packer_piecework', <PackerCapturePage />)} />
      {/* Bag balances (mig 20261248500000): bags issued vs empties clocked back, per packer. */}
      <Route path="/packer-capture/bags"   element={gPack('packer_piecework', <PieceBagReportPage />)} />
      {/* Face kiosk — also reachable while signed in, for admin to demo or test (mig 541) */}
      <Route path="/clock-face"            element={<ClockFacePage />} />
      {/* Face match log — admin/manager only by RLS on worker_face_match_log.
          Two routes so the back-arrow returns to the right Labour page. */}
      <Route path="/admin/face-match-log"       element={gLabour('labour',      <FaceMatchLogPage />)} />
      <Route path="/farm/admin/face-match-log"  element={gFarm('farm_labour', <FaceMatchLogPage />)} />
      <Route path="/labour/config"         element={gLabour('labour', <LabourConfigPage />)} />
      <Route path="/farm/labour"           element={gFarm('farm_labour', <FarmLabourPage />)} />
      <Route path="/farm/labour/config"    element={gFarm('farm_labour', <LabourConfigPage />)} />
      <Route path="/payslip/:lineId"       element={gLabour('labour', <MoneyOnly label="Payslip"><PayslipPage /></MoneyOnly>)} />
      <Route path="/payroll/:periodId/payslips-print" element={gLabour('labour', <MoneyOnly label="Payslips"><PayslipsBulkPage /></MoneyOnly>)} />
      <Route path="/payroll/:periodId/attendance-print" element={gLabour('labour', <MoneyOnly label="Attendance & piecework sheets"><AttendanceBulkPage /></MoneyOnly>)} />
      <Route path="/labour/attendance-report" element={gLabour('labour', <MoneyOnly label="Attendance & piecework report"><AttendanceReportPage /></MoneyOnly>)} />
      {/* Clockings + units only, no rand — not behind MoneyOnly. */}
      <Route path="/labour/employee-report" element={gLabour('labour', <EmployeeDayReportPage />)} />
      {/* Labour → Activities → Print. Rands only for a reader who may see pay,
          money unlocked, as on the tab — so not behind MoneyOnly. */}
      <Route path="/labour/activities-report" element={gLabour('labour', <ActivitiesReportPage />)} />
      {/* Who left and why — no rand, so not behind MoneyOnly. Its sibling lists
          the leave paid out to them, which is money. Mig 20261025183000. */}
      <Route path="/labour/termination-report" element={gLabour('labour', <TerminationReportPage />)} />
      {/* Employment equity (EEA2) counts — labour managers only (race is POPIA s26). Phase 1: read-only. */}
      <Route path="/labour/ee-report" element={gLabour('labour', <EeReportPage />)} />
      <Route path="/labour/termination-leave-pay" element={gLabour('labour', <MoneyOnly label="Termination leave pay report"><TerminationLeavePayPage /></MoneyOnly>)} />
      {/* BigScan roll utilisation — packhouse only (QR rolls are a packhouse
          piecework consumable; there is no farm equivalent). */}
      <Route path="/labour/roll-utilisation"  element={gLabour('labour', <RollUtilisationPage />)} />
      {/* Punnet register — packhouse only, for the same reason: it reads the
          packout tally rail (the farm's picking rail is harvest_pick_tallies). */}
      <Route path="/labour/piecework-report"  element={gLabour('labour', <PieceworkReportPage />)} />
      <Route path="/payroll/:periodId/register" element={gLabour('labour', <MoneyOnly label="Payroll register"><PayrollRegisterPage /></MoneyOnly>)} />
      {/* EMP201 reconciliation — the per-employee listing behind the month's
          return. Month + worker group ride in the query string so the EMP201
          panel can link straight to the month it is showing. */}
      <Route path="/payroll/emp201-recon" element={gLabour('labour', <MoneyOnly label="EMP201 reconciliation"><Emp201ReconPage /></MoneyOnly>)} />
      <Route path="/labour/worker/:workerNumber/earnings" element={gLabour('labour', <MoneyOnly label="Worker earnings"><WorkerEarningsPage /></MoneyOnly>)} />
      {/* One packer against themselves + against peers. Manager-gated. */}
      <Route path="/labour/worker/:workerNumber/performance" element={gLabour('labour', <PackerPerformancePage />)} />
      {/* Farm-tenant mirrors. Labour.tsx mounts at /farm/labour for FIELD
          workers; the payslip + bulk-print links flip prefix by category so
          FARM_ADMINs aren't denied by the gPack gate on the pack mirror. */}
      <Route path="/farm/payslip/:lineId"  element={gFarm('farm_labour', <MoneyOnly label="Payslip"><PayslipPage /></MoneyOnly>)} />
      <Route path="/farm/payroll/:periodId/payslips-print" element={gFarm('farm_labour', <MoneyOnly label="Payslips"><PayslipsBulkPage /></MoneyOnly>)} />
      <Route path="/farm/payroll/:periodId/attendance-print" element={gFarm('farm_labour', <MoneyOnly label="Attendance & piecework sheets"><AttendanceBulkPage /></MoneyOnly>)} />
      <Route path="/farm/labour/attendance-report" element={gFarm('farm_labour', <MoneyOnly label="Attendance & piecework report"><AttendanceReportPage /></MoneyOnly>)} />
      <Route path="/farm/labour/employee-report" element={gFarm('farm_labour', <EmployeeDayReportPage />)} />
      <Route path="/farm/labour/activities-report" element={gFarm('farm_labour', <ActivitiesReportPage />)} />
      <Route path="/farm/labour/termination-report" element={gFarm('farm_labour', <TerminationReportPage />)} />
      <Route path="/farm/labour/ee-report" element={gFarm('farm_labour', <EeReportPage />)} />
      <Route path="/farm/labour/termination-leave-pay" element={gFarm('farm_labour', <MoneyOnly label="Termination leave pay report"><TerminationLeavePayPage /></MoneyOnly>)} />
      <Route path="/farm/payroll/:periodId/register" element={gFarm('farm_labour', <MoneyOnly label="Payroll register"><PayrollRegisterPage /></MoneyOnly>)} />
      <Route path="/farm/payroll/emp201-recon" element={gFarm('farm_labour', <MoneyOnly label="EMP201 reconciliation"><Emp201ReconPage /></MoneyOnly>)} />
      <Route path="/farm/labour/worker/:workerNumber/earnings" element={gFarm('farm_labour', <MoneyOnly label="Worker earnings"><WorkerEarningsPage /></MoneyOnly>)} />
      {/* BCEA s29 written particulars — printable artefact for a single
          worker_particulars row. Both packhouse + farm tenants reach this
          via the same RLS-gated route; gating is on worker_particulars RLS
          rather than the module gate (so farm managers can print field
          worker particulars without needing the gPack 'labour' module). */}
      <Route path="/labour/particulars/:particularsId/print" element={<ParticularsPrintPage />} />
      {/* Typed worker contract (registry PR-3) — printable + kiosk-signable
          artefact for a single worker_contracts row. Same RLS-gated-not-
          module-gated model as the s29 particulars route above. */}
      <Route path="/labour/contracts/:contractId/print" element={<ContractPrintPage />} />
      {/* BCEA s42 certificate of service — printable point-in-time doc from the
          worker's current record (no snapshot). Same RLS-gated route model. */}
      <Route path="/labour/certificate-of-service/:workerNumber/print" element={<MoneyOnly label="Certificate of service"><CertificateOfServicePage /></MoneyOnly>} />
      {/* The UI-2.7 as a labelled sheet beside the certificate (P02 UF-14). Same
          route model; MoneyOnly because it prints six months' pay. The RPC's
          can_manage_labour() gate is the boundary. */}
      <Route path="/labour/ui27/:workerNumber/print" element={<MoneyOnly label="UI-2.7 sheet"><Ui27SheetPage /></MoneyOnly>} />
      {/* Injury on duty (mig 20261223000000): prints monthly earnings, so MoneyOnly like the UI-2.7. */}
      <Route path="/labour/injury/:reportId/print" element={<MoneyOnly label="W.Cl.2 sheet"><InjuryWcl2Page /></MoneyOnly>} />
      {/* Disciplinary warning letter — printable + acknowledgeable artefact for a
          single worker_warnings row. Same RLS-gated-not-module-gated model as the
          s29 particulars route above (packhouse + farm managers both reach it). */}
      <Route path="/labour/warnings/:warningId/print" element={<WarningLetterPrintPage />} />
      {/* Authed users hitting the share URL get the same anonymous-view page. */}
      <Route path="/payslip-share/:token"  element={<PayslipSharePage />} />
      {/* Worker portal also reachable when authed — for managers testing the link. */}
      <Route path="/worker-portal/:token"  element={<WorkerPortalPage />} />
      {/* Packing-contract signing also reachable when authed — a manager testing the link. */}
      <Route path="/sign/:token"           element={<PackingContractSignPage />} />
      {/* Buyer shipment tracker / portal also reachable when authed — for exporters testing the link. */}
      <Route path="/track/:token"          element={<BuyerTrackPage />} />
      {/* Same single portal route as the anon block — see PortalRoute. */}
      <Route path="/portal/:portalId"      element={<PortalRoute />} />
      <Route path="/invoice"    element={gPack('financial', <InvoicePage />)} />
      <Route path="/grower-statement"            element={gPack('financial', <GrowerStatementPage />)} />
      <Route path="/grower-statement/:growerId"  element={gPack('financial', <GrowerStatementPage />)} />
      <Route path="/grower-season-summary/:growerId" element={gPack('financial', <GrowerSeasonSummaryPage />)} />
      <Route path="/growers-season-summary" element={gPack('financial', <GrowersSeasonSummaryPage />)} />
      <Route path="/transport-invoice" element={gPack('financial', <TransportInvoicePage />)} />
      <Route path="/transport-recon"   element={gPack('transport_recon', <TransportReconPage />)} />
      <Route path="/proof/:consignmentId" element={gPack('consignments', <ProofOfSubmissionPage />)} />
      <Route path="/consignments" element={gPack('consignments', <ConsignmentsPage />)} />
      <Route path="/ppecb-certificates" element={gPack('consignments', <PpecbCertificatesPage />)} />
      <Route path="/dispatch-note/:missionId" element={gPack('dispatch', <DispatchNotePage />)} />
      <Route path="/dispatch-note/:missionId/stop/:stopId" element={gPack('dispatch', <DispatchNotePage />)} />
      <Route path="/consignment-note/:consignmentId" element={gPack('consignments', <ConsignmentNotePage />)} />
      <Route path="/farm/sprays"            element={gFarm('sprays',            <SpraysPage />)} />
      <Route path="/farm/weather"           element={gFarm('farm_weather',      <FarmWeatherPage />)} />
      <Route path="/farm/fert-applications" element={gFarm('fert_applications', <FertApplicationsPage />)} />
      <Route path="/farm/fert-applications/report" element={gFarm('fert_applications', <FertReportPage />)} />
      <Route path="/farm/phenology"         element={gFarm('phenology',         <PhenologyPage />)} />
      <Route path="/farm/estimates"         element={gFarm('farm_estimates',    <FarmEstimatesPage />)} />
      <Route path="/farm/equipment"         element={gFarm('farm_equipment',    <FarmEquipmentPage />)} />
      <Route path="/farm/logbook"           element={gFarm('farm_logbook',      <VehicleLogbookPage />)} />
      <Route path="/farm/fuel"              element={gFarm('farm_fuel',         <FuelPage />)} />
      <Route path="/farm/fuel/report"       element={gFarm('farm_fuel',         <FuelReportPage />)} />
      <Route path="/farm/utilities"         element={gFarm('farm_utilities',    <FarmUtilitiesPage />)} />
      <Route path="/farm/field-ops"         element={gFarm('farm_field_ops',    <FarmFieldOpsPage />)} />
      <Route path="/farm/seeds"             element={gFarm('farm_seed',         <FarmSeedsPage />)} />
      <Route path="/farm/compliance"           element={gFarm('farm_compliance', <FarmCompliancePage />)} />
      <Route path="/farm/siza"                 element={gFarm('farm_compliance', <SizaAssessmentPage />)} />
      {/* Shared compliance-management engine (checklists, documents, training,
          equipment, HSE, non-conformances, renewals) — same component the
          packhouse mounts at /compliance. Tenant-scoped tables + RLS isolate
          farm data. backTo returns to the farm Audit Pack landing. */}
      <Route path="/farm/compliance/manage"    element={gFarm('farm_compliance', <CompliancePage backTo="/farm/compliance" />)} />
      <Route path="/farm/compliance/audit-pack" element={gFarm('farm_compliance', <ComplianceAuditPackPage />)} />
      <Route path="/farm/compliance/docs"      element={gFarm('farm_compliance', <FarmComplianceDocsPage />)} />
      <Route path="/farm/compliance/:blockId"  element={gFarm('farm_compliance', <FarmComplianceBlockReportPage />)} />

      {/* Agent app — Phase 5 UI. Routes gated by gAgent (tenant.kind=AGENT
          + module flag). v1 shipped /agent/visits + detail builder + SEND;
          v2 adds the catalogue + recipes + config pages so an agent can
          maintain everything they need without platform-admin help. */}
      <Route path="/agent/visits"             element={gAgent('agent_visits',   <AgentVisitsPage />)} />
      <Route path="/agent/visits/:visitId"    element={gAgent('agent_visits',   <AgentVisitDetailPage />)} />
      <Route path="/agent/products"           element={gAgent('agent_products', <AgentProductsPage />)} />
      <Route path="/agent/recipes"            element={gAgent('agent_recipes',  <AgentRecipesPage />)} />
      <Route path="/agent/config"             element={gAgent('agent_config',   <AgentConfigPage />)} />

      {/* Supplier app (BigSuppliers) — SUPPLIER-kind tenants only. The
          catalogue is private; deliveries + customers ride the
          linked_supplier tenant_relationships row. */}
      <Route path="/supplier/products"        element={gSupplier('supplier_products',   <SupplierProductsPage />)} />
      <Route path="/supplier/deliveries"      element={gSupplier('supplier_deliveries', <SupplierDeliveriesPage />)} />
      <Route path="/supplier/maintenance-requests" element={gSupplier('supplier_maintenance_requests', <SupplierMaintReqPage />)} />
      <Route path="/supplier/customers"       element={gSupplier('supplier_customers',  <SupplierCustomersPage />)} />
      <Route path="/supplier/config"          element={gSupplier('supplier_config',     <SupplierConfigPage />)} />

      {/* Transporter app (BigLogistics P0) — TRANSPORTER-kind tenants only.
          P0 = identity + consent: the carrier can see who it's linked to and
          manage its own members. Trips / fleet / availability / PODs land in
          P1–P3 (docs/specs/biglogistics.md). */}
      {/* BigAgri billing (PLATFORM-kind tenant only) */}
      <Route path="/bigagri/billing"          element={gPlatform('saas_billing',        <SaasBillingPage />)} />
      <Route path="/bigagri/billing/statement/:tenantId" element={gPlatform('saas_billing', <SaasStatementPrintPage />)} />

      <Route path="/transporter/trips"        element={gTrans('transporter_trips',      <TransporterTripsPage />)} />
      <Route path="/transporter/fleet"        element={gTrans('transporter_fleet',      <TransporterFleetPage />)} />
      <Route path="/transporter/availability" element={gTrans('transporter_availability', <TransporterAvailabilityPage />)} />
      <Route path="/transporter/partners"     element={gTrans('transporter_partners',   <TransporterPartnersPage />)} />
      <Route path="/transporter/config"       element={gTrans('transporter_config',     <TransporterConfigPage />)} />

      {/* Harvest app (BigHarvest) — HARVEST-kind tenants only. */}
      <Route path="/harvest/clients"          element={gHarvest('harvest_clients',      <HarvestClientsPage />)} />
      <Route path="/harvest/capture"          element={gHarvest('harvest_capture',      <HarvestCapturePage />)} />
      <Route path="/harvest/day-end"          element={gHarvest('harvest_dayend',       <HarvestDayEndPage />)} />
      <Route path="/harvest/monitor"          element={gHarvest('harvest_monitor',      <HarvestMonitorPage />)} />
      <Route path="/harvest/picking-map"      element={gHarvest('harvest_picking_map', <HarvestPickingMapPage />)} />
      <Route path="/harvest/spans"            element={gHarvest('harvest_spans',        <HarvestSpansPage />)} />
      <Route path="/harvest/workers"          element={gHarvest('harvest_workers',      <HarvestWorkersPage />)} />
      {/* The printable Mon–Sun sheet behind Attendance → "Week timesheet". Same
          page as the packhouse/farm mirrors, but NOT MoneyOnly: on HARVEST it
          renders hours and units with no rand at all (D10/D11), so there is no
          money to withhold — and withholding it would break the only route the
          Attendance tab offers. Keep it in step with attendanceReportPath(). */}
      <Route path="/harvest/attendance-report" element={gHarvest('harvest_workers',      <AttendanceReportPage />)} />
      <Route path="/harvest/rates"            element={gHarvest('harvest_rates',        <HarvestRatesPage />)} />
      <Route path="/harvest/destinations"     element={gHarvest('harvest_destinations', <HarvestDestinationsPage />)} />
      <Route path="/harvest/tasks"            element={gHarvest('harvest_tasks',        <HarvestTasksPage />)} />
      <Route path="/harvest/config"           element={gHarvest('harvest_config',       <HarvestConfigPage />)} />

      {/* Security app (BigSecurity) — SECURITY-kind tenants only. */}
      <Route path="/bigsecurity/sites"        element={gSecurity('security_sites',      <SecuritySitesPage />)} />
      <Route path="/bigsecurity/config"       element={gSecurity('security_config',     <SecurityConfigPage />)} />
      <Route path="/bigsecurity/incidents"    element={gSecurity('security_operations', <SecurityIncidentRegisterPage />)} />
      <Route path="/bigsecurity/incidents/:incidentId" element={gSecurity('security_operations', <SecurityIncidentLink />)} />
      <Route path="/bigsecurity/incidents/:incidentId/print" element={gSecurity('security_operations', <SecurityIncidentPrintPage />)} />

      {/* Exporter app (BigExport) — EXPORTER-kind tenants only (Anchor Agri SA
          et al). Export desk: loads register, derived stock-on-hand, packing
          lists, packouts, identity/config. Standalone module — no packhouse
          state-machine / tipping / QC. See docs/EXPORTER-BUILD-PLAN.md. */}
      <Route path="/exporter"                 element={gExporter('exporter_shipments',     <ExporterOverviewPage />)} />
      <Route path="/exporter/shipments"       element={gExporter('exporter_shipments',     <ExporterShipmentsPage />)} />
      <Route path="/exporter/shipments/:shipmentId" element={gExporter('exporter_shipments', <ExporterShipmentDetailPage />)} />
      <Route path="/exporter/loads"           element={gExporter('exporter_loads',         <ExporterLoadsPage />)} />
      <Route path="/exporter/stock"           element={gExporter('exporter_stock',         <ExporterStockPage />)} />
      <Route path="/exporter/packhouse-pallets" element={gExporter('exporter_packhouse_pallets', <ExporterPackhousePalletsPage />)} />
      <Route path="/exporter/partner-stock"   element={gExporter('exporter_partner_stock',   <ExporterPartnerStockPage />)} />
      <Route path="/exporter/trade-sales"     element={gExporter('exporter_trade_sales',     <ExporterTradeSalesPage />)} />
      <Route path="/exporter/tracking"        element={gExporter('exporter_shipments',     <ExporterTrackingPage />)} />
      <Route path="/exporter/packing-lists"   element={gExporter('exporter_packing_lists', <ExporterPackingListsPage />)} />
      <Route path="/exporter/packouts"        element={gExporter('exporter_packouts',      <ExporterPackoutsPage />)} />
      <Route path="/exporter/invoices"        element={gExporter('exporter_invoices',      <ExporterInvoicesPage />)} />
      <Route path="/exporter/orders"          element={gExporter('exporter_sales_orders',  <ExporterSalesOrdersPage />)} />
      <Route path="/exporter/config"          element={gExporter('exporter_config',        <ExporterConfigPage />)} />
      <Route path="/exporter/receivers"       element={gExporter('exporter_receivers',     <ExporterReceiversPage />)} />
      <Route path="/exporter/receivables"     element={gExporter('exporter_receivers',     <ExporterReceivablesPage />)} />
      <Route path="/exporter/statement"       element={gExporter('exporter_receivers',     <ExporterStatementPage />)} />
      <Route path="/exporter/shipping-agents" element={gExporter('exporter_config',        <ExporterShippingAgentsPage />)} />
      <Route path="/buyer"                    element={gBuyer(<BuyerDashboardPage />)} />
      <Route path="/buyer/market"             element={gBuyerM('buyer_market', <BuyerMarketPage />)} />
      <Route path="/exporter/members"         element={gExporter('exporter_members',       <ExporterMembersPage />)} />
      <Route path="/exporter/organogram"      element={gExporter('exporter_organogram',    <OrganogramPage />)} />
      <Route path="/exporter/activity"        element={gExporter('exporter_activity',      <ExporterActivityPage />)} />

      {/* Franchise admin — Phase 5v2 polish. Routes gated by gFranchise
          (AGENT tenant + has franchise_id + user role FRANCHISE_ADMIN).
          The landing page overviews the franchise, lists member agents,
          and links into the master chemical catalogue editor. */}
      <Route path="/franchise"                  element={gFranchise('franchise_admin', <FranchiseAdminPage />)} />
      <Route path="/franchise/chemicals"        element={gFranchise('franchise_admin', <FranchiseChemicalsPage />)} />
      <Route path="/franchise/agents/:tenantId" element={gFranchise('franchise_admin', <FranchiseAgentDetailPage />)} />
      {/* Legacy spray paths — three modules consolidated into /farm/sprays
          with tabs (Plan / My tasks / Records). Redirect existing
          bookmarks + any old in-app links so they continue to work. */}
      <Route path="/farm/spray-tasks"    element={<Navigate to="/farm/sprays?tab=plan" replace />} />
      <Route path="/farm/my-spray-tasks" element={<Navigate to="/farm/sprays?tab=mine" replace />} />
      <Route path="/farm/spray-records"  element={<Navigate to="/farm/sprays?tab=records" replace />} />
      <Route path="/farm/spray-recipes"  element={gFarm('sprays',         <SprayRecipesPage />)} />
      <Route path="/farm/sprays/map"     element={gFarm('sprays',         <SprayMapPage />)} />
      <Route path="/farm/sprays/report"  element={gFarm('sprays',         <SprayReportPage />)} />
      <Route path="/farm/sprays/driving" element={gFarm('sprays',         <SprayDrivingPage />)} />
      <Route path="/farm/sprays/:sprayId/print" element={gFarm('sprays',  <SprayNotePage />)} />
      <Route path="/farm/map"           element={gFarm('farm_map',      <FarmMapPage />)} />
      <Route path="/farm/cost-report"   element={gFarm('farm_cost_report', <FarmCostReportPage />)} />
      <Route path="/farm/market-prices" element={gFarm('farm_market_prices', <MarketPricesPage />)} />
      <Route path="/farm/budget"        element={gFarm('farm_budget', <FarmBudgetPage />)} />
      <Route path="/farm/risk-profile"  element={gFarm('farm_risk_profile', <FarmRiskProfilePage />)} />
      <Route path="/farm/water-schedule" element={gFarm('water_schedule',   <WaterSchedulePage />)} />
      <Route path="/farm/water-schedule/pulse-report" element={gFarm('water_schedule', <PulseReportPage />)} />
      <Route path="/farm/drain-monitoring" element={gFarm('drain_monitoring', <DrainMonitoringPage />)} />
      <Route path="/farm/drain-report"     element={gFarm('drain_monitoring', <DrainReportPage />)} />
      <Route path="/farm/analyses"       element={gFarm('farm_analyses',    <FarmAnalysesPage />)} />
      <Route path="/farm/receipts"      element={gFarm('farm_receipts',    <FarmReceiptsPage />)} />
      <Route path="/farm/suppliers"     element={gFarm('farm_suppliers',   <FarmSuppliersPage />)} />
      <Route path="/farm/harvest-picks" element={gFarm('harvest_picks', <HarvestPicksPage />)} />
      <Route path="/farm/loads" element={gFarm('farm_loads', <FarmLoadsPage />)} />
      <Route path="/farm/harvest-picks/report" element={gFarm('harvest_picks', <HarvestPicksReportPage />)} />
      <Route path="/farm/teams"             element={gFarm('farm_teams',    <FarmTeamsPage />)} />
      <Route path="/farm/team-performance"  element={gFarm('farm_team_performance', <FarmTeamPerformancePage />)} />
      <Route path="/farm/piecework-config"  element={gFarmPieceRates(<PieceworkConfigPage />)} />
      <Route path="/farm/blocks"        element={gFarm('farm_orchard',  <OrchardPage />)} />
      {/* Renamed "My Orchard" → "My Blocks"; keep the old path redirecting so existing bookmarks/links survive. */}
      <Route path="/farm/orchard"       element={<Navigate to="/farm/blocks" replace />} />
      <Route path="/farm/scouting"        element={gFarm('scouting',      <ScoutingPage />)} />
      <Route path="/farm/problems"        element={gFarm('farm_problems', <FarmProblemsPage />)} />
      <Route path="/farm/traps"           element={gFarm('farm_traps', <FarmTrapsPage />)} />
      <Route path="/farm/report-injury"    element={gFarm('farm_safety_report', <FarmReportIncidentPage />)} />
      <Route path="/farm/organogram"      element={gFarm('farm_organogram', <OrganogramPage />)} />
      <Route path="/farm/calendar"        element={gFarm('farm_calendar', <FarmCalendarPage />)} />
      <Route path="/farm/scouting/map"    element={gFarm('scouting',      <ScoutingMapPage />)} />
      <Route path="/farm/scouting/trends" element={gFarm('scouting',      <ScoutingTrendsPage />)} />
      <Route path="/farm/scouting/print"        element={gFarm('scouting',  <ScoutReportPage />)} />
      {/* The one-page summary. Same module key as the report — it quotes the
          same rows, so anyone who may not read those may not read a count of
          them either. */}
      <Route path="/farm/scouting/summary"      element={gFarm('scouting',  <ScoutSummaryPage />)} />
      <Route path="/farm/phenology/:blockId/print" element={gFarm('phenology', <PhenologyReportPage />)} />
      <Route path="/farm/activity"      element={gFarm('farm_activity', <FarmActivityPage />)} />
      <Route path="/farm/members"       element={gFarm('farm_members',  <FarmMembersPage />)} />
      <Route path="/farm/config"        element={gFarm('farm_config',   <FarmConfigPage />)} />
      <Route path="/farm/security"      element={gFarm('farm_security', <SecurityIncidentsPage />)} />
      <Route path="/farm/security/tags" element={gFarm('farm_security', <SecurityCheckpointTagsPage />)} />
      <Route path="/farm/security/:incidentId/print" element={gFarm('farm_security', <SecurityIncidentPrintPage />)} />
      <Route path="/farm/settings"      element={gFarm('farm_config',   <FarmSettingsPage />)} />
      {/* FM02 UF-17: anyone with Scouting opens Scout targets — scouts read
          it, managers edit it (the page's own gate and RLS). It was gated on
          Farm Config, which FARM_MANAGER lacks: the person the alerts go to
          could not tune them. */}
      <Route path="/farm/scout-targets" element={gFarm('scouting',      <ScoutTargetsPage />)} />
      <Route path="/farm/commodities"   element={gFarm('farm_config',   <FarmCommoditiesPage />)} />
      <Route path="/farm/chemicals"     element={gFarm('chemicals',     <ChemicalsPage />)} />
      <Route path="/farm/stock"            element={gFarm('farm_stock', <FarmStockPage />)} />
      <Route path="/farm/ppe"              element={gFarm('farm_ppe',   <FarmPpePage />)} />
      <Route path="/farm/stock/categories" element={gFarm('farm_stock', <FarmStockCategoriesPage />)} />
      <Route path="/farm/stock/owners"     element={gFarm('farm_stock', <StockOwnersPage backTo="/farm/stock" />)} />
      <Route path="/notifications" element={<NotificationsPage />} />
      <Route path="/notifications/settings" element={<NotificationSettingsPage />} />
      {/* Training — every tile's guide + video. Deliberately ungated, like
          /notifications: it is documentation, not tenant data, and it must be
          findable by a user whose role or tenant plan hides every other tile. */}
      <Route path="/training" element={<TrainingPage />} />
      <Route path="/training/:guideId" element={<TrainingGuidePage />} />
      <Route path="/recall" element={gPack('recall', <RecallPage />)} />
      <Route path="/recall/day" element={gPack('recall', <RecallDayPage />)} />
      {/* Network-upload diagnostic. Not gated to any module so it's
          reachable from a slow tablet without an active membership
          flap. Operator opens /diag/upload from their phone vs
          tablet to compare per-device timings. */}
      <Route path="/diag/upload" element={<UploadDiagnosticPage />} />
      {/* Grower portals also reachable while logged-in (handy for Manager preview) */}
      <Route path="/g/:runId"   element={<GrowerPortalPage />} />
      <Route path="*"           element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>

    {/* ⌘K / Ctrl-K command palette — navigate anywhere + search help. Owns the
        shortcut globally; the Dashboard header has a labelled trigger, and it
        delegates to the pallet/bin modal below via "Find pallet / bin…". No
        floating FAB — it stole the phone thumb zone. */}
    <HelpBarRoot />

    {/* Pallet/bin lookup modal — opened from the palette or its header button. */}
    <PalletSearchRoot />

    {/* Support-ticket modal — the single global instance. Opened from the ⌘K
        palette's "Report a problem…" (every route) and from the help drawer's
        escalation section. Mounted here because its previous home, <HelpButton/>,
        is on 5 of 228 routes — which is why support_tickets sat at zero rows. */}
    <BugReportRoot />

    {/* "Talk to support" — the in-app support call (voice or typed), opened from
        the palette. One instance; it can point at tagged buttons on any page. */}
    <SupportCallRoot />

    {/* Persistent bottom tab bar for the operator's hot screens (touch only,
        PACKHOUSE/FARM only) — one-tap sibling-task switching, supplements the
        dashboard hub. Reserves its own space via the has-bottom-nav body class. */}
    <BottomNav />

    {/* TEMP diagnostic for the standalone-PWA nav drift — off unless ?navdebug=1 */}
    <NavDebugChip />

    {/* Dunning / trial-conversion strip — only renders while payment is failed
        or an enrolled trial is ending (SaaS billing Phase C) */}
    <BillingBanner />

    {/* Offline-queue indicator — only renders when there's something to show */}
    <OfflineQueueBadge />

    {/* Undo toast — one app-level instance; showUndo() from lib/undoToast opens it */}
    <UndoToast />

    {/* Pull-to-refresh — invalidates every TanStack query on release */}
    <PullToRefresh />

    {/* PWA update prompt — shows when a new SW has been installed */}
    <UpdateBanner />

    {/* PWA install prompt — captures beforeinstallprompt + iOS hint */}
    <InstallPrompt />
    </TenantStatusGate>
    </ConsentGate>
    </LockoutGate>
    {/* Vercel Web Analytics — tracks page views and user interactions */}
    <Analytics />
    {/* Vercel Speed Insights — tracks performance metrics */}
    <SpeedInsights />
    </>
  )
}
